import {
  BASE_SHIELD_SECONDS,
  BULLET_SIZE,
  CELL_SIZE,
  CLASSIC_ALIGN_SPEED,
  CLASSIC_LANE_SNAP,
  DIR_VECTORS,
  PLAYER_INVINCIBLE_SECONDS,
  PLAYER_MAX_HP,
  PLAYER_RESPAWN_SECONDS,
  PLAYER_SPECS,
  POWERUP_SECONDS,
  COLS,
  ROWS,
  TANK_SIZE,
  VIEW_HEIGHT,
  VIEW_WIDTH,
} from './constants';
import {
  boundsAt,
  canTankOccupy,
  cellsForRect,
  collidesWithAnyTank,
  findSafeSpawn,
  moveTankWithCollision,
  resolveTankOverlaps,
  tankBoundsAt,
  tileBlocksBullet,
} from './collision';
import { BOSS_VARIANT_PROFILES, ENEMY_ARCHETYPES, buildEnemySpawnQueue, type BossVariantProfile } from '../content/enemies';
import { cloneMap, getLevelDefinition, placeBase } from '../content/levels';
import { t } from '../i18n';
import { applyPlayerUpgradeStats, DEFAULT_PLAYER_TANK_CLASS, PLAYER_TANK_CLASS_SPECS, downgradePlayerOnDeath, getPlayerTankClassSpec, getPlayerUpgradeStats, getTankBulletDamage, MAX_PLAYER_UPGRADE_LEVEL, normalizePlayerTankClass, upgradePlayerTank } from './progression';
import { getSpriteMuzzleLateralOffset } from './spriteMetrics';
import {
  TileType,
  type BattleDecalEntity,
  type BulletEntity,
  type ControlState,
  type Direction,
  type EnemyArchetype,
  type EnemyKind,
  type EnvironmentKind,
  type ExplosionEffectEntity,
  type ExplosionEffectKind,
  type GameEvent,
  type GameModeConfig,
  type GameStats,
  type GameStatus,
  type InputSnapshot,
  type LevelDefinition,
  type ParticleEntity,
  type PlayerTankClass,
  type PlayerNumber,
  type PowerupEntity,
  type PowerupKind,
  type BossVariant,
  type TankEntity,
  type UpcomingEnemyPreview,
  type Vec2,
} from './types';
import { approach, directionFromInput, directionToward, makeId, pick, rectsOverlap, vectorFor } from './utils';

const DEFAULT_MODE: GameModeConfig = {
  infiniteLives: false,
  invincibleBase: false,
  friendlyFire: false,
  upgradeDropRate: 'normal',
  loseUpgradeOnDeath: false,
  playerCount: 2,
  levelTransitionDelay: 5,
  obstacleDensity: 'high',
  initialPlayerLevel: 1,
  enemyAiMode: 'classic',
  playerTankClasses: [
    DEFAULT_PLAYER_TANK_CLASS,
    DEFAULT_PLAYER_TANK_CLASS,
    DEFAULT_PLAYER_TANK_CLASS,
    DEFAULT_PLAYER_TANK_CLASS,
  ],
};

const POWERUP_KINDS: PowerupKind[] = ['rapid', 'shield', 'bomb', 'life', 'freeze', 'repair', 'baseShield', 'jam', 'upgrade', 'heal'];
const MAX_CONTINUES = 3;
const BOMB_BOSS_DAMAGE = 4;
const MIN_LEVEL_TRANSITION_DELAY = 2;
const MAX_LEVEL_TRANSITION_DELAY = 10;
const CONTINUE_RESERVED_UPGRADE_DROPS = 2;
const BASE_TARGET: Vec2 = { x: 9.5 * CELL_SIZE, y: 13.5 * CELL_SIZE };
const PLAYER_SPAWN_FALLBACKS: Vec2[] = [
  { x: 3.5 * CELL_SIZE, y: (ROWS - 1.5) * CELL_SIZE },
  { x: (COLS - 3.5) * CELL_SIZE, y: (ROWS - 1.5) * CELL_SIZE },
  { x: 5.5 * CELL_SIZE, y: (ROWS - 1.5) * CELL_SIZE },
  { x: (COLS - 5.5) * CELL_SIZE, y: (ROWS - 1.5) * CELL_SIZE },
];
const PLAYER_ARMOR_PIERCING_SHELL = {
  damageMultiplier: 1.75,
  minDamage: 3.25,
  brickPierce: 3,
  metalPierce: 1,
  sizeBonus: 2.2,
};
const PLAYER_EXPLOSIVE_SHELLS = {
  level3: {
    damageMultiplier: 1.32,
    minDamage: 2.25,
    radius: CELL_SIZE * 1.45,
    splashDamage: 0.76,
    lightSplashDamage: 1.15,
    tileDamage: 1.15,
    sizeBonus: 2.6,
  },
  level4: {
    damageMultiplier: 1.42,
    minDamage: 2.55,
    radius: CELL_SIZE * 1.65,
    splashDamage: 0.94,
    lightSplashDamage: 1.35,
    tileDamage: 1.35,
    sizeBonus: 3.1,
  },
};
const PLAYER_TRACER_SHELLS = {
  level3: {
    damageMultiplier: 1.12,
    minDamage: 1.55,
    speedMultiplier: 1.28,
    suppressSeconds: 0.58,
    bossSuppressMultiplier: 0.38,
    sizeBonus: -0.25,
  },
  level4: {
    damageMultiplier: 1.2,
    minDamage: 1.8,
    speedMultiplier: 1.34,
    suppressSeconds: 0.72,
    bossSuppressMultiplier: 0.42,
    sizeBonus: -0.1,
  },
};
type SpecialShellKind = 'armorPiercing' | 'explosive' | 'tracer';
export class GameEngine {
  status: GameStatus = 'menu';
  mode: GameModeConfig = { ...DEFAULT_MODE };
  level = 1;
  score = 0;
  p1Lives = 3;
  p2Lives = 3;
  p3Lives = 0;
  p4Lives = 0;
  map: TileType[][] = getLevelDefinition(1).map;
  levelDef: LevelDefinition = getLevelDefinition(1);
  players: TankEntity[] = [];
  enemies: TankEntity[] = [];
  bullets: BulletEntity[] = [];
  powerups: PowerupEntity[] = [];
  particles: ParticleEntity[] = [];
  decals: BattleDecalEntity[] = [];
  effects: ExplosionEffectEntity[] = [];
  spawnQueue: UpcomingEnemyPreview[] = [];
  enemiesToSpawn = 0;
  enemiesSpawned = 0;
  spawnTimer = 0;
  baseDestroyed = false;
  baseHp = 3;
  baseShieldTimer = 0;
  jamTimer = 0;
  levelTimer = 0;
  muted = false;
  tileHp = new Map<string, number>();
  powerupDropCooldown = 0;
  continuesRemaining = MAX_CONTINUES;
  levelCountdownBeep = 0;
  debugPlayerInvincible = false;
  debugBaseInvincible = false;

  private events: GameEvent[] = [];
  private gameOverReason: 'baseDestroyed' | 'outOfLives' | null = null;
  private reservedUpgradeDrops = 0;

  constructor() {
    this.preparePreview();
  }

  preparePreview(): void {
    this.levelDef = getLevelDefinition(1);
    this.map = cloneMap(this.levelDef.map);
    this.players = [];
    this.enemies = [];
    this.bullets = [];
    this.powerups = [];
    this.particles = [];
    this.decals = [];
    this.effects = [];
    this.spawnQueue = [];
    this.baseDestroyed = false;
    this.continuesRemaining = MAX_CONTINUES;
    this.reservedUpgradeDrops = 0;
    this.gameOverReason = null;
    this.initializeTileHp();
  }

  startNewGame(mode: Partial<GameModeConfig>): void {
    this.mode = {
      ...DEFAULT_MODE,
      ...mode,
      levelTransitionDelay: clampLevelTransitionDelay(mode.levelTransitionDelay ?? DEFAULT_MODE.levelTransitionDelay),
      initialPlayerLevel: clampPlayerUpgradeLevel(mode.initialPlayerLevel ?? DEFAULT_MODE.initialPlayerLevel),
      obstacleDensity: mode.obstacleDensity ?? DEFAULT_MODE.obstacleDensity,
      playerTankClasses: normalizePlayerTankClasses(mode.playerTankClasses ?? DEFAULT_MODE.playerTankClasses),
    };
    this.level = 1;
    this.score = 0;
    this.resetPlayerLives();
    this.continuesRemaining = MAX_CONTINUES;
    this.baseShieldTimer = mode.invincibleBase ? Number.POSITIVE_INFINITY : 0;
    this.jamTimer = 0;
    this.powerupDropCooldown = 0;
    this.reservedUpgradeDrops = 0;
    this.gameOverReason = null;
    this.status = 'playing';
    this.players = [];
    this.startLevel(1);
  }

  restartLevel(): void {
    this.startLevel(this.level);
  }

  debugJumpToLevel(level: number): void {
    const targetLevel = Math.max(1, Math.min(99, Math.round(level) || 1));
    this.startLevel(targetLevel);
  }

  debugHealPlayers(): void {
    for (const player of this.players) {
      if (!player.alive) continue;
      player.hp = player.maxHp;
      player.smokeTimer = 0;
    }
    this.pushMessage(t('dev.toast.healed'));
  }

  debugSetPlayerUpgradeLevel(level: number): void {
    const targetLevel = clampPlayerUpgradeLevel(level);
    for (const player of this.players) {
      player.upgradeLevel = targetLevel;
      player.specialShotCounter = 0;
      applyPlayerUpgradeStats(player);
      player.hp = player.maxHp;
      player.smokeTimer = 0;
    }
    this.pushMessage(t('dev.toast.upgrade', { level: targetLevel }));
  }

  debugSpawnPowerup(kind: PowerupKind): void {
    const anchor = this.players.find((player) => player.alive) ?? this.players[0] ?? BASE_TARGET;
    this.powerupDropCooldown = 0;
    this.spawnPowerup(anchor.x, anchor.y, kind);
    this.pushMessage(t('dev.toast.powerup', { powerup: t(`powerup.${kind}`) }));
  }

  debugClearEnemies(): void {
    let cleared = 0;
    for (const enemy of [...this.enemies]) {
      if (!enemy.alive) continue;
      this.damageTank(enemy, enemy.hp);
      cleared += 1;
    }
    this.enemiesToSpawn = 0;
    if (cleared > 0) this.pushMessage(t('dev.toast.clear', { count: cleared }));
  }

  debugSpawnEnemy(kind: EnemyKind): void {
    this.debugSpawnEnemyVariant(kind);
  }

  debugSpawnEnemyVariant(kind: EnemyKind, bossVariant?: BossVariant): void {
    const archetype = ENEMY_ARCHETYPES[kind];
    const fallback = this.levelDef.spawnPoints[0] ?? { x: 9.5 * CELL_SIZE, y: 1.5 * CELL_SIZE };
    const spawn = findSafeSpawn(fallback, this.map, this.liveTanks, archetype.size);
    if (!canTankOccupy(this.map, spawn.x, spawn.y, archetype.size) || collidesWithAnyTank(tankBoundsAt(spawn.x, spawn.y, archetype.size), this.liveTanks)) {
      this.pushMessage(t('dev.toast.spawnBlocked'));
      return;
    }
    this.enemies.push(this.createEnemy(archetype, spawn, bossVariant));
    const enemyLabel = kind === 'boss' && bossVariant
      ? `${t(`bossVariant.${bossVariant}`)} Boss`
      : t(`dev.enemy.${kind}`);
    this.pushMessage(t('dev.toast.spawnEnemy', { enemy: enemyLabel }));
  }

  debugAdvanceFrame(dt: number, input: InputSnapshot): void {
    if (this.status !== 'paused') return;
    this.status = 'playing';
    this.update(dt, { ...input, pausePressed: false });
    if (this.status === 'playing') this.status = 'paused';
  }

  debugSetPlayerInvincible(enabled: boolean): void {
    this.debugPlayerInvincible = enabled;
    this.pushMessage(t(enabled ? 'dev.toast.playerInvincibleOn' : 'dev.toast.playerInvincibleOff'));
  }

  debugSetBaseInvincible(enabled: boolean): void {
    this.debugBaseInvincible = enabled;
    this.pushMessage(t(enabled ? 'dev.toast.baseInvincibleOn' : 'dev.toast.baseInvincibleOff'));
  }

  debugDamageBase(): void {
    this.baseDestroyed = false;
    this.baseHp = Math.max(1, this.baseHp - 1);
    for (let row = 0; row < ROWS; row += 1) {
      for (let col = 0; col < COLS; col += 1) {
        if (this.map[row][col] !== TileType.BaseWall) continue;
        const maxHp = this.getTileMaxHp(TileType.BaseWall);
        this.tileHp.set(tileKey(row, col), Math.max(1, maxHp - 2));
        this.pushMessage(t('dev.toast.baseDamaged'));
        return;
      }
    }
    this.pushMessage(t('dev.toast.baseDamaged'));
  }

  togglePause(): void {
    if (this.status === 'playing') {
      this.status = 'paused';
      this.pushMessage(t('message.paused'));
    } else if (this.status === 'paused') {
      this.status = 'playing';
      this.pushMessage(t('message.resume'));
    }
  }

  update(dt: number, input: InputSnapshot): void {
    if (input.pausePressed) this.togglePause();
    if (this.status === 'paused' || this.status === 'menu' || this.status === 'gameOver') {
      this.updateParticles(dt);
      this.updateEffects(dt);
      this.updateDecals(dt);
      return;
    }
    if (this.status === 'levelComplete') {
      this.levelTimer -= dt;
      this.updateGlobalTimers(dt);
      this.updateIntermissionPlayers(dt, input);
      this.updatePowerups(dt);
      this.updateParticles(dt);
      this.updateEffects(dt);
      this.updateDecals(dt);
      this.updateLevelCountdownBeeps();
      if (this.levelTimer <= 0) this.startLevel(this.level + 1);
      return;
    }

    this.updateGlobalTimers(dt);
    this.updatePlayers(dt, input);
    this.updateEnemies(dt);
    resolveTankOverlaps(this.liveTanks, this.map);
    this.updateBullets(dt);
    this.updatePowerups(dt);
    this.updateDamageSmoke(dt);
    this.updateParticles(dt);
    this.updateDecals(dt);
    this.updateEffects(dt);
    this.updateSpawning(dt);
    this.checkLevelComplete();
  }

  drainEvents(): GameEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }

  getStats(): GameStats {
    const p1 = this.players[0];
    const p2 = this.players[1];
    const playerStats = this.getPlayerHudStats();
    return {
      level: this.level,
      score: this.score,
      p1Lives: this.p1Lives,
      p2Lives: this.p2Lives,
      p1Hp: p1 ? (p1.alive ? p1.hp : 0) : PLAYER_MAX_HP,
      p1MaxHp: p1?.maxHp ?? PLAYER_MAX_HP,
      p1UpgradeLevel: p1?.upgradeLevel ?? 1,
      p2Hp: p2 ? (p2.alive ? p2.hp : 0) : PLAYER_MAX_HP,
      p2MaxHp: p2?.maxHp ?? PLAYER_MAX_HP,
      p2UpgradeLevel: p2?.upgradeLevel ?? 1,
      players: playerStats,
      enemiesRemaining: this.enemiesToSpawn + this.enemies.filter((enemy) => enemy.alive).length,
      baseState: this.getBaseState(),
      status: this.status,
      continuesRemaining: this.continuesRemaining,
      playerCount: this.mode.playerCount,
      upcomingWave: this.spawnQueue.slice(0, 3),
    };
  }

  getEnvironment(): EnvironmentKind {
    const cycle = (this.level - 1) % 3;
    if (cycle === 0) return 'desert';
    if (cycle === 1) return 'forest';
    return 'snow';
  }

  getTileHp(row: number, col: number): number {
    const tile = this.map[row]?.[col] ?? TileType.Empty;
    if (tile === TileType.BaseCore) return Math.max(0, this.baseHp);
    return this.tileHp.get(tileKey(row, col)) ?? this.getTileMaxHp(tile);
  }

  getTileMaxHp(tile: TileType): number {
    if (tile === TileType.BaseCore || tile === TileType.BaseWall || tile === TileType.ReinforcedWall) return 3;
    if (tile === TileType.Brick) return 1;
    return 0;
  }

  setTileHp(row: number, col: number, hp: number): void {
    const maxHp = this.getTileMaxHp(this.map[row]?.[col] ?? TileType.Empty);
    if (maxHp <= 0) return;
    this.tileHp.set(tileKey(row, col), Math.max(0, Math.min(maxHp, hp)));
  }

  get liveTanks(): TankEntity[] {
    return [...this.players, ...this.enemies].filter((tank) => tank.alive);
  }

  private resetPlayerLives(): void {
    for (const playerNum of [1, 2, 3, 4] as PlayerNumber[]) {
      const active = playerNum <= this.mode.playerCount;
      this.setPlayerLives(playerNum, active ? (this.mode.infiniteLives ? 999 : 3) : 0);
    }
  }

  private getPlayerHudStats(): GameStats['players'] {
    return ([1, 2, 3, 4] as PlayerNumber[]).map((playerNum, index) => {
      const player = this.players[index];
      const active = playerNum <= this.mode.playerCount;
      return {
        playerNum,
        lives: this.getPlayerLives(playerNum),
        hp: player && player.alive ? player.hp : 0,
        maxHp: player?.maxHp ?? PLAYER_MAX_HP,
        upgradeLevel: player?.upgradeLevel ?? 1,
        active,
      };
    });
  }

  private getPlayerLives(playerNum: PlayerNumber): number {
    if (playerNum === 1) return this.p1Lives;
    if (playerNum === 2) return this.p2Lives;
    if (playerNum === 3) return this.p3Lives;
    return this.p4Lives;
  }

  private setPlayerLives(playerNum: PlayerNumber, lives: number): void {
    if (playerNum === 1) this.p1Lives = lives;
    else if (playerNum === 2) this.p2Lives = lives;
    else if (playerNum === 3) this.p3Lives = lives;
    else this.p4Lives = lives;
  }

  private playerSpawnAt(index: number): Vec2 {
    return this.levelDef.playerSpawns[index] ?? PLAYER_SPAWN_FALLBACKS[index] ?? PLAYER_SPAWN_FALLBACKS[0];
  }

  private clearPlayerSpawnZones(): void {
    for (let index = 0; index < this.mode.playerCount; index += 1) {
      this.clearSpawnZone(this.playerSpawnAt(index));
    }
  }

  private clearSpawnZone(spawn: Vec2): void {
    const bounds = tankBoundsAt(spawn.x, spawn.y, TANK_SIZE + 8);
    const cells = cellsForRect(bounds);
    for (let row = cells.startRow; row <= cells.endRow; row += 1) {
      for (let col = cells.startCol; col <= cells.endCol; col += 1) {
        if (row < 0 || row >= ROWS || col < 0 || col >= COLS) continue;
        if (this.map[row][col] === TileType.BaseCore) continue;
        this.map[row][col] = TileType.Empty;
        this.tileHp.delete(`${row},${col}`);
      }
    }
  }

  private getScaledEnemyTotal(baseTotal: number): number {
    const extraPlayers = Math.max(0, this.mode.playerCount - 2);
    if (extraPlayers === 0) return baseTotal;
    const multiplier = extraPlayers === 1 ? 1.18 : 1.34;
    return Math.min(36, Math.ceil(baseTotal * multiplier) + extraPlayers);
  }

  private getMaxEnemiesOnScreen(): number {
    return Math.min(10, this.levelDef.maxEnemiesOnScreen + Math.max(0, this.mode.playerCount - 2));
  }

  private getMaxPowerupsOnField(): number {
    return Math.max(2, Math.min(4, this.mode.playerCount));
  }

  isBaseProtected(): boolean {
    return this.mode.invincibleBase || this.debugBaseInvincible || this.baseShieldTimer > 0;
  }

  getGameOverReasonText(): string {
    if (this.gameOverReason === 'baseDestroyed') return t('gameOver.baseDestroyed');
    if (this.gameOverReason === 'outOfLives') return t('gameOver.outOfLives');
    return t('overlay.gameOver.title');
  }

  continueCurrentLevel(): boolean {
    if (this.status !== 'gameOver' || this.continuesRemaining <= 0) return false;
    this.continuesRemaining -= 1;
    this.resetPlayerLives();
    this.baseShieldTimer = this.mode.invincibleBase ? Number.POSITIVE_INFINITY : 0;
    this.players = [];
    this.startLevel(this.level, { preserveUpgrades: false });
    this.reservedUpgradeDrops = this.mode.upgradeDropRate === 'off' ? 0 : Math.min(this.mode.playerCount, CONTINUE_RESERVED_UPGRADE_DROPS);
    this.pushMessage(t('message.continue', { count: this.continuesRemaining }));
    return true;
  }

  abandonRun(): void {
    this.status = 'menu';
    this.preparePreview();
  }

  private startLevel(level: number, options: { preserveUpgrades?: boolean } = {}): void {
    this.level = level;
    this.levelDef = getLevelDefinition(level, this.mode.obstacleDensity);
    this.map = cloneMap(this.levelDef.map);
    this.enemies = [];
    this.bullets = [];
    this.powerups = [];
    this.particles = [];
    this.decals = [];
    this.effects = [];
    this.spawnQueue = buildEnemySpawnQueue(level, this.getScaledEnemyTotal(this.levelDef.totalEnemies));
    this.enemiesToSpawn = this.spawnQueue.length;
    this.enemiesSpawned = 0;
    this.spawnTimer = 1.4;
    this.baseDestroyed = false;
    this.baseHp = 3;
    this.powerupDropCooldown = 0;
    this.levelCountdownBeep = 0;
    this.gameOverReason = null;
    this.status = 'playing';
    if (this.mode.invincibleBase) this.baseShieldTimer = Number.POSITIVE_INFINITY;
    this.initializeTileHp();
    this.clearPlayerSpawnZones();
    const previousPlayers = options.preserveUpgrades === false ? [] : this.players;
    this.players = PLAYER_SPECS.slice(0, this.mode.playerCount).map((_spec, index) => {
      const player = this.createPlayer(index);
      const previous = previousPlayers[index];
      if (previous && previous.upgradeLevel > player.upgradeLevel) {
        player.upgradeLevel = previous.upgradeLevel;
        applyPlayerUpgradeStats(player);
        player.hp = player.maxHp;
      }
      return player;
    });
    this.pushMessage(t('message.level', { level }));
    this.events.push({ kind: 'level' });
  }

  private createPlayer(index: number): TankEntity {
    const spec = PLAYER_SPECS[index];
    const playerTankClass = normalizePlayerTankClass(this.mode.playerTankClasses?.[index]);
    const classSpec = PLAYER_TANK_CLASS_SPECS[playerTankClass];
    const spawn = this.playerSpawnAt(index);
    const player: TankEntity = {
      id: spec.id,
      team: 'player',
      kind: 'player',
      playerNum: spec.playerNum,
      color: spec.color,
      accent: spec.accent,
      x: spawn.x,
      y: spawn.y,
      vx: 0,
      vy: 0,
      size: TANK_SIZE,
      direction: 'up',
      alive: true,
      hp: PLAYER_MAX_HP,
      maxHp: PLAYER_MAX_HP,
      upgradeLevel: this.mode.initialPlayerLevel,
      maxSpeed: spec.maxSpeed,
      acceleration: 820,
      friction: 1000,
      fireRate: spec.fireRate,
      baseFireRate: spec.fireRate,
      bulletSpeed: spec.bulletSpeed,
      fireCooldown: 0,
      fireBuffer: 0,
      rapidTimer: 0,
      invincibleTimer: PLAYER_INVINCIBLE_SECONDS,
      shieldTimer: 0,
      freezeTimer: 0,
      jamTimer: 0,
      respawnTimer: 0,
      aiTimer: 0,
      aiDirection: 'up',
      stuckTimer: 0,
      maneuverTimer: 0,
      dodgeCooldownTimer: 0,
      snapShotCooldownTimer: 0,
      smokeTimer: 0,
      shootWindupTimer: 0,
      scoreValue: 0,
      lastMoveBlocked: false,
      specialShotCounter: 0,
      turnBias: 1,
      debugAiLabel: '',
      playerTankClass,
    };
    player.maxSpeed *= classSpec.maxSpeedMultiplier;
    player.acceleration *= classSpec.accelerationMultiplier;
    applyPlayerUpgradeStats(player);
    player.hp = player.maxHp;
    return player;
  }

  private createEnemy(kind: EnemyArchetype, spawn: Vec2, bossVariant?: BossVariant): TankEntity {
    const variant = kind.kind === 'boss' ? this.getBossVariantProfile(bossVariant) : null;
    const minFireRate = kind.kind === 'boss' ? variant?.minFireRate ?? 0.68 : kind.kind === 'heavy' ? 1.05 : 1.45;
    const fireRate = Math.max(minFireRate, (kind.fireRate + 0.18 - this.level * 0.008) * (variant?.fireRateMultiplier ?? 1));
    const levelSpeedBonus = kind.kind === 'heavy'
      ? Math.min(10, this.level * 0.55)
      : kind.kind === 'boss'
        ? Math.min(8, this.level * 0.35)
        : Math.min(20, this.level * 1.1);
    const bulletSpeed = (kind.kind === 'heavy' ? kind.bulletSpeed * 0.7 : kind.bulletSpeed) * (variant?.bulletSpeedMultiplier ?? 1);
    const hp = kind.hp + (variant?.hpBonus ?? 0);
    return {
      id: makeId('enemy'),
      team: 'enemy',
      kind: kind.kind,
      color: kind.color,
      accent: kind.accent,
      x: spawn.x,
      y: spawn.y,
      vx: 0,
      vy: 0,
      size: kind.size,
      direction: 'down',
      alive: true,
      hp,
      maxHp: hp,
      upgradeLevel: 1,
      maxSpeed: kind.maxSpeed + levelSpeedBonus + (variant?.speedBonus ?? 0),
      acceleration: 600,
      friction: 820,
      fireRate,
      baseFireRate: fireRate,
      bulletSpeed,
      fireCooldown: 2.0 + Math.random() * 1.0,
      fireBuffer: 0,
      rapidTimer: 0,
      invincibleTimer: 0.75,
      shieldTimer: 0,
      freezeTimer: 0,
      jamTimer: 0,
      respawnTimer: 0,
      aiTimer: 0,
      aiDirection: 'down',
      stuckTimer: 0,
      maneuverTimer: 0,
      dodgeCooldownTimer: 0,
      snapShotCooldownTimer: 0,
      smokeTimer: 0,
      shootWindupTimer: 0,
      scoreValue: kind.score,
      lastMoveBlocked: false,
      specialShotCounter: 0,
      turnBias: 1,
      debugAiLabel: '',
      bossVariant: variant ? bossVariant ?? 'siege' : undefined,
    };
  }

  private updateGlobalTimers(dt: number): void {
    if (Number.isFinite(this.baseShieldTimer)) this.baseShieldTimer = Math.max(0, this.baseShieldTimer - dt);
    this.jamTimer = Math.max(0, this.jamTimer - dt);
    this.powerupDropCooldown = Math.max(0, this.powerupDropCooldown - dt);
  }

  private updatePlayers(dt: number, input: InputSnapshot): void {
    this.players.forEach((player, index) => {
      this.updateTankTimers(player, dt);
      if (!player.alive) {
        player.respawnTimer -= dt;
        if (player.respawnTimer <= 0) this.tryRespawnPlayer(index);
        return;
      }
      const controls = controlsForPlayerInput(input, index);
      this.updatePlayerMovement(player, controls, dt);
      if (controls.fire) player.fireBuffer = 0.14;
      this.tryFire(player);
    });
    this.checkNoLivesGameOver();
  }

  private updatePlayerMovement(player: TankEntity, controls: ControlState, dt: number): void {
    if (player.freezeTimer > 0) return;
    const direction = directionFromInput(controls.up, controls.down, controls.left, controls.right, player.direction);
    this.moveClassicTank(player, direction, dt);
  }

  private updateIntermissionPlayers(dt: number, input: InputSnapshot): void {
    this.players.forEach((player, index) => {
      this.updateTankTimers(player, dt);
      if (!player.alive) {
        player.respawnTimer -= dt;
        if (player.respawnTimer <= 0) this.tryRespawnPlayer(index);
        return;
      }
      const controls = controlsForPlayerInput(input, index);
      this.updatePlayerMovement(player, controls, dt);
    });
  }

  private updateEnemies(dt: number): void {
    for (const enemy of this.enemies) {
      this.updateTankTimers(enemy, dt);
      if (!enemy.alive) continue;
      if (enemy.freezeTimer > 0) {
        enemy.debugAiLabel = 'FREEZE';
        this.stopTank(enemy);
        continue;
      }
      if (enemy.shootWindupTimer > 0) {
        enemy.debugAiLabel = `WIND ${this.getEnemyTargetLabel(enemy)}`;
        enemy.shootWindupTimer = Math.max(0, enemy.shootWindupTimer - dt);
        this.stopTank(enemy);
        if (enemy.shootWindupTimer === 0 && this.jamTimer <= 0) {
          enemy.fireBuffer = 0.1;
          this.tryFire(enemy);
        }
        continue;
      }
      const bossCloseShotDirection = enemy.kind === 'boss' ? this.getBossPointBlankShotDirection(enemy) : null;
      if (bossCloseShotDirection && this.jamTimer <= 0) {
        const nextDirection = this.getBossTurnDirection(enemy, bossCloseShotDirection, this.getBossPriorityTarget(enemy));
        const aligned = nextDirection === bossCloseShotDirection;
        enemy.debugAiLabel = `PBLANK ${this.getEnemyTargetLabel(enemy)}`;
        enemy.direction = nextDirection;
        enemy.aiDirection = nextDirection;
        enemy.aiTimer = Math.max(enemy.aiTimer, aligned ? 0.26 : this.getBossVariantProfile(enemy.bossVariant).turnReactionSeconds + Math.random() * 0.24);
        this.stopTank(enemy);
        if (aligned && enemy.fireCooldown <= 0) enemy.shootWindupTimer = this.getEnemyShotWindup(enemy, true);
        continue;
      }
      const bossSnapReverseShotDirection = enemy.kind === 'boss' ? this.getBossSnapReverseShotDirection(enemy) : null;
      if (bossSnapReverseShotDirection && this.jamTimer <= 0 && enemy.fireCooldown <= 0) {
        enemy.debugAiLabel = `SNAP ${this.getEnemyTargetLabel(enemy)}`;
        enemy.direction = bossSnapReverseShotDirection;
        enemy.aiDirection = bossSnapReverseShotDirection;
        enemy.snapShotCooldownTimer = this.getBossVariantProfile(enemy.bossVariant).snapReverseShotCooldownSeconds;
        enemy.shootWindupTimer = this.getEnemyShotWindup(enemy);
        this.stopTank(enemy);
        continue;
      }
      const bossShotDirection = enemy.kind === 'boss' ? this.getBossShotDirection(enemy) : null;
      if (bossShotDirection && this.jamTimer <= 0 && enemy.fireCooldown <= 0) {
        const nextDirection = this.getBossTurnDirection(enemy, bossShotDirection, this.getBossPriorityTarget(enemy));
        if (nextDirection !== bossShotDirection) {
          enemy.debugAiLabel = `AIM ${this.getEnemyTargetLabel(enemy)}`;
          enemy.direction = nextDirection;
          enemy.aiDirection = nextDirection;
          enemy.aiTimer = this.getBossVariantProfile(enemy.bossVariant).turnReactionSeconds + Math.random() * 0.34;
          enemy.fireCooldown = Math.max(enemy.fireCooldown, this.getBossVariantProfile(enemy.bossVariant).turnReactionSeconds);
          this.stopTank(enemy);
          continue;
        }
        if (enemy.direction !== nextDirection) {
          enemy.debugAiLabel = `AIM ${this.getEnemyTargetLabel(enemy)}`;
          enemy.direction = nextDirection;
          enemy.aiDirection = nextDirection;
          enemy.aiTimer = this.getBossVariantProfile(enemy.bossVariant).turnReactionSeconds + Math.random() * 0.28;
          enemy.fireCooldown = Math.max(enemy.fireCooldown, this.getBossVariantProfile(enemy.bossVariant).turnReactionSeconds);
          this.stopTank(enemy);
          continue;
        }
        enemy.debugAiLabel = `FIRE ${this.getEnemyTargetLabel(enemy)}`;
        enemy.shootWindupTimer = this.getEnemyShotWindup(enemy);
        this.stopTank(enemy);
        continue;
      }
      if (enemy.kind === 'boss' && this.tryBossProjectileDodge(enemy, dt)) continue;
      this.updateEnemyDecision(enemy, dt);
      this.moveClassicTank(enemy, enemy.aiDirection, dt);
      enemy.stuckTimer = enemy.lastMoveBlocked
        ? Math.min(2.5, enemy.stuckTimer + dt)
        : Math.max(0, enemy.stuckTimer - dt * (enemy.kind === 'boss' ? 1.8 : 0.6));
      if (enemy.lastMoveBlocked || enemy.stuckTimer > 0.18) {
        if (enemy.kind === 'boss') {
          this.handleBossBlockedMovement(enemy);
        } else {
          enemy.aiDirection = this.chooseBlockedEnemyDirection(enemy);
          enemy.aiTimer = this.getEnemyBlockedRetargetDelay(enemy);
          enemy.stuckTimer = 0;
          enemy.debugAiLabel = `BLOCK ${enemy.aiDirection.toUpperCase()}`;
        }
      }
      const wantsShot = this.wantsForwardPlayerShot(enemy, dt)
        || (enemy.kind !== 'boss' && this.hasForwardLineOfSightToExposedBaseCore(enemy))
        || this.wantsBossScreenedPlayerShot(enemy, dt)
        || this.wantsRandomClearShot(enemy, dt);
      if (wantsShot && this.jamTimer <= 0 && enemy.fireCooldown <= 0) {
        enemy.debugAiLabel = `LOCK ${this.getEnemyTargetLabel(enemy)}`;
        enemy.shootWindupTimer = this.getEnemyShotWindup(enemy);
        this.stopTank(enemy);
      }
    }
  }

  private moveClassicTank(tank: TankEntity, direction: Direction | null, dt: number): void {
    tank.lastMoveBlocked = false;
    if (!direction) {
      this.stopTank(tank);
      return;
    }

    tank.direction = direction;
    this.alignToClassicLane(tank, direction, dt);
    const vec = DIR_VECTORS[direction];
    const beforeX = tank.x;
    const beforeY = tank.y;
    const dx = vec.x * tank.maxSpeed * dt;
    const dy = vec.y * tank.maxSpeed * dt;
    tank.vx = vec.x * tank.maxSpeed;
    tank.vy = vec.y * tank.maxSpeed;
    moveTankWithCollision(tank, this.map, this.liveTanks, dx, dy);
    const moved = Math.abs(tank.x - beforeX) > 0.01 || Math.abs(tank.y - beforeY) > 0.01;
    if (!moved) {
      tank.lastMoveBlocked = true;
      this.stopTank(tank);
    }
  }

  private stopTank(tank: TankEntity): void {
    tank.vx = 0;
    tank.vy = 0;
  }

  private alignToClassicLane(tank: TankEntity, direction: Direction, dt: number): void {
    const horizontal = direction === 'left' || direction === 'right';
    const axis = horizontal ? 'y' : 'x';
    const target = Math.round((tank[axis] - CELL_SIZE / 2) / CELL_SIZE) * CELL_SIZE + CELL_SIZE / 2;
    const diff = target - tank[axis];
    if (Math.abs(diff) <= CLASSIC_LANE_SNAP) {
      tank[axis] = approach(tank[axis], target, CLASSIC_ALIGN_SPEED * dt);
    }
  }

  private updateTankTimers(tank: TankEntity, dt: number): void {
    tank.fireCooldown = Math.max(0, tank.fireCooldown - dt);
    tank.fireBuffer = Math.max(0, tank.fireBuffer - dt);
    tank.invincibleTimer = Math.max(0, tank.invincibleTimer - dt);
    tank.shieldTimer = Math.max(0, tank.shieldTimer - dt);
    tank.freezeTimer = Math.max(0, tank.freezeTimer - dt);
    tank.jamTimer = Math.max(0, tank.jamTimer - dt);
    tank.rapidTimer = Math.max(0, tank.rapidTimer - dt);
    tank.fireRate = tank.rapidTimer > 0 ? tank.baseFireRate * 0.55 : tank.baseFireRate;
    tank.stuckTimer = Math.max(0, tank.stuckTimer - dt * 0.6);
    tank.maneuverTimer = Math.max(0, tank.maneuverTimer - dt);
    tank.dodgeCooldownTimer = Math.max(0, tank.dodgeCooldownTimer - dt);
    tank.snapShotCooldownTimer = Math.max(0, tank.snapShotCooldownTimer - dt);
  }

  private updateEnemyDecision(enemy: TankEntity, dt: number): void {
    enemy.aiTimer -= dt;
    if (enemy.aiTimer > 0) return;
    const arch = ENEMY_ARCHETYPES[enemy.kind as keyof typeof ENEMY_ARCHETYPES];
    if (enemy.kind === 'boss') {
      this.updateBossDecision(enemy);
      return;
    }
    const enhanced = this.mode.enemyAiMode === 'enhanced';
    enemy.aiTimer = enhanced ? 0.92 + Math.random() * 1.18 : 1.25 + Math.random() * 1.6;
    const roll = Math.random();
    if (enemy.kind === 'sniper' && this.hasForwardLineOfSightToPlayer(enemy)) {
      return;
    }
    const directPlayerDirection = enhanced ? this.directionTowardNearbyPlayer(enemy) : null;
    if (directPlayerDirection && roll < 0.28 + arch.aimChance * 0.18) {
      enemy.aiDirection = directPlayerDirection;
      enemy.debugAiLabel = `CHASE ${this.getEnemyTargetLabel(enemy)}`;
    } else if (roll < 0.52) {
      enemy.aiDirection = 'down';
      enemy.debugAiLabel = 'DRIFT DOWN';
    } else if (roll < 0.52 + arch.aggression * 0.28) {
      enemy.aiDirection = directionToward(enemy, BASE_TARGET);
      enemy.debugAiLabel = 'PRESS BASE';
    } else {
      enemy.aiDirection = pick(['up', 'down', 'left', 'right'] as Direction[]);
      enemy.debugAiLabel = `PATROL ${enemy.aiDirection.toUpperCase()}`;
    }
  }

  private updateBossDecision(enemy: TankEntity): void {
    const target = this.getBossPriorityTarget(enemy);
    const variant = this.getBossVariantProfile(enemy.bossVariant);
    enemy.aiTimer = this.mode.enemyAiMode === 'enhanced'
      ? variant.decisionMinSeconds + Math.random() * 0.46
      : variant.decisionMinSeconds + 0.18 + Math.random() * 0.62;
    enemy.aiDirection = this.chooseBossAdvanceDirection(enemy, target);
    enemy.debugAiLabel = `ADV ${this.debugTargetLabel(target)}`;
  }

  private handleBossBlockedMovement(enemy: TankEntity): void {
    const variant = this.getBossVariantProfile(enemy.bossVariant);
    if (enemy.maneuverTimer > 0 && enemy.stuckTimer < variant.reverseStuckSeconds) {
      enemy.aiTimer = Math.max(enemy.aiTimer, 0.24);
      enemy.debugAiLabel = `GRIND ${enemy.aiDirection.toUpperCase()}`;
      return;
    }
    const previousDirection = enemy.direction;
    const nextDirection = this.chooseBossBlockedDirection(enemy, this.getBossPriorityTarget(enemy), enemy.stuckTimer >= variant.reverseStuckSeconds);
    enemy.aiDirection = nextDirection;
    enemy.direction = nextDirection;
    enemy.turnBias = nextDirection === leftTurn(previousDirection) ? -1 : nextDirection === rightTurn(previousDirection) ? 1 : enemy.turnBias;
    enemy.maneuverTimer = nextDirection === oppositeDirection(previousDirection) ? 0.22 : variant.blockCommitSeconds + Math.random() * 0.22;
    enemy.aiTimer = nextDirection === oppositeDirection(previousDirection) ? 0.42 : 0.74 + Math.random() * 0.28;
    enemy.stuckTimer = nextDirection === oppositeDirection(previousDirection) ? 0 : Math.min(enemy.stuckTimer, 0.42);
    enemy.debugAiLabel = `BLOCK ${nextDirection.toUpperCase()}`;
  }

  private chooseBlockedEnemyDirection(enemy: TankEntity): Direction {
    const target = enemy.kind === 'boss'
      ? this.getBossPriorityTarget(enemy)
      : this.getEnemyStrategicTarget(enemy);
    const towardBase = directionToward(enemy, target);
    const candidates = uniqueDirections([
      towardBase,
      enemy.direction === 'up' ? 'left' : 'down',
      ...perpendicularDirections(enemy.direction),
      oppositeDirection(enemy.direction),
    ]);
    for (const direction of candidates) {
      if (direction !== enemy.direction && this.canEnemyMoveToward(enemy, direction)) return direction;
    }
    return candidates.find((direction) => this.canEnemyMoveToward(enemy, direction)) ?? oppositeDirection(enemy.direction);
  }

  private canEnemyMoveToward(enemy: TankEntity, direction: Direction): boolean {
    const vec = DIR_VECTORS[direction];
    const probeDistance = CELL_SIZE * 0.42;
    const x = enemy.x + vec.x * probeDistance;
    const y = enemy.y + vec.y * probeDistance;
    const rect = tankBoundsAt(x, y, enemy.size);
    return canTankOccupy(this.map, x, y, enemy.size) && !collidesWithAnyTank(rect, this.liveTanks, enemy.id);
  }

  private getEnemyStrategicTarget(enemy: TankEntity): Vec2 {
    if (this.mode.enemyAiMode !== 'enhanced') return BASE_TARGET;
    const nearbyPlayer = this.getNearestLivingPlayer(enemy);
    if (!nearbyPlayer) return BASE_TARGET;
    return nearbyPlayer.distance <= CELL_SIZE * 6.5 ? nearbyPlayer.player : BASE_TARGET;
  }

  private directionTowardNearbyPlayer(enemy: TankEntity): Direction | null {
    const nearest = this.getNearestLivingPlayer(enemy);
    if (!nearest || nearest.distance > CELL_SIZE * 6.5) return null;
    return directionToward(enemy, nearest.player);
  }

  private getNearestLivingPlayer(enemy: TankEntity): { player: TankEntity; distance: number } | null {
    const livingPlayers = this.players.filter((player) => player.alive);
    if (livingPlayers.length === 0) return null;
    return livingPlayers
      .map((player) => ({ player, distance: Math.hypot(player.x - enemy.x, player.y - enemy.y) }))
      .sort((a, b) => a.distance - b.distance)[0] ?? null;
  }

  private getBossVariantProfile(variant?: BossVariant): BossVariantProfile {
    return BOSS_VARIANT_PROFILES[variant ?? 'siege'];
  }

  private getBossPriorityTarget(enemy: TankEntity): Vec2 {
    const livingPlayers = this.players.filter((player) => player.alive);
    if (livingPlayers.length === 0) return BASE_TARGET;
    const nearest = this.getNearestLivingPlayer(enemy);
    if (!nearest) return BASE_TARGET;
    const visiblePlayers = livingPlayers
      .filter((player) => this.lineOfSightDirectionToPoint(enemy, player, CELL_SIZE * 0.42))
      .sort((a, b) => Math.hypot(a.x - enemy.x, a.y - enemy.y) - Math.hypot(b.x - enemy.x, b.y - enemy.y));
    return visiblePlayers[0] ?? nearest.player;
  }

  private getEnemyTargetLabel(enemy: TankEntity): string {
    if (enemy.kind === 'boss') return this.debugTargetLabel(this.getBossPriorityTarget(enemy));
    if (this.mode.enemyAiMode !== 'enhanced') return 'BASE';
    const nearest = this.getNearestLivingPlayer(enemy);
    if (!nearest || nearest.distance > CELL_SIZE * 6.5) return 'BASE';
    return `P${nearest.player.playerNum ?? 1}`;
  }

  private debugTargetLabel(target: Vec2): string {
    const player = this.players.find((candidate) => candidate === target);
    if (player) return `P${player.playerNum ?? 1}`;
    return 'BASE';
  }

  private getBossShotDirection(enemy: TankEntity): Direction | null {
    const target = this.getBossPriorityTarget(enemy);
    return this.lineOfSightDirectionToPoint(enemy, target, CELL_SIZE * 0.42);
  }

  private getBossSnapReverseShotDirection(enemy: TankEntity): Direction | null {
    const variant = this.getBossVariantProfile(enemy.bossVariant);
    if (variant.snapReverseShotCooldownSeconds <= 0 || enemy.snapShotCooldownTimer > 0) return null;
    const target = this.getBossPriorityTarget(enemy);
    const direction = this.lineOfSightDirectionToPoint(enemy, target, CELL_SIZE * 0.42);
    return direction === oppositeDirection(enemy.direction) ? direction : null;
  }

  private getBossPointBlankShotDirection(enemy: TankEntity): Direction | null {
    const pointBlankRange = enemy.size / 2 + TANK_SIZE / 2 + this.getBossVariantProfile(enemy.bossVariant).pointBlankExtraRange;
    const nearest = this.players
      .filter((player) => player.alive)
      .map((player) => ({ player, distance: Math.hypot(player.x - enemy.x, player.y - enemy.y) }))
      .filter(({ distance }) => distance <= pointBlankRange)
      .sort((a, b) => a.distance - b.distance)[0]?.player;
    if (!nearest) return null;
    return directionToward(enemy, nearest);
  }

  private chooseDirectionTowardPoint(enemy: TankEntity, target: Vec2): Direction {
    const dx = target.x - enemy.x;
    const dy = target.y - enemy.y;
    const primary: Direction = Math.abs(dx) > Math.abs(dy)
      ? (dx < 0 ? 'left' : 'right')
      : (dy < 0 ? 'up' : 'down');
    const secondary: Direction = Math.abs(dx) > Math.abs(dy)
      ? (dy < 0 ? 'up' : 'down')
      : (dx < 0 ? 'left' : 'right');
    const candidates = uniqueDirections([primary, secondary, ...perpendicularDirections(primary), oppositeDirection(primary)]);
    return candidates.find((direction) => this.canEnemyMoveToward(enemy, direction)) ?? primary;
  }

  private chooseBossAdvanceDirection(enemy: TankEntity, target: Vec2): Direction {
    if (enemy.bossVariant === 'sniper') {
      const nearest = this.getNearestLivingPlayer(enemy);
      const preferredRange = this.getBossVariantProfile(enemy.bossVariant).preferredRangeCells * CELL_SIZE;
      if (nearest && nearest.distance < preferredRange) return this.chooseBossRetreatDirection(enemy, nearest.player);
    }
    const desired = this.chooseDirectionTowardPoint(enemy, target);
    return this.getBossTurnDirection(enemy, desired, target);
  }

  private chooseBossRetreatDirection(enemy: TankEntity, threat: Vec2): Direction {
    const dx = enemy.x - threat.x;
    const dy = enemy.y - threat.y;
    const primary: Direction = Math.abs(dx) > Math.abs(dy)
      ? (dx < 0 ? 'left' : 'right')
      : (dy < 0 ? 'up' : 'down');
    const secondary: Direction = Math.abs(dx) > Math.abs(dy)
      ? (dy < 0 ? 'up' : 'down')
      : (dx < 0 ? 'left' : 'right');
    const candidates = uniqueDirections([primary, secondary, ...perpendicularDirections(primary), enemy.direction]);
    return candidates.find((direction) => this.canEnemyMoveToward(enemy, direction)) ?? primary;
  }

  private getBossTurnDirection(enemy: TankEntity, desired: Direction, target: Vec2): Direction {
    if (desired === enemy.direction) return desired;
    const reverse = oppositeDirection(enemy.direction);
    if (desired !== reverse) return desired;
    const preferred = this.chooseBossLateralDirection(enemy, target);
    const alternate = preferred === leftTurn(enemy.direction) ? rightTurn(enemy.direction) : leftTurn(enemy.direction);
    if (this.canEnemyMoveToward(enemy, preferred)) return preferred;
    if (this.canEnemyMoveToward(enemy, alternate)) return alternate;
    return preferred;
  }

  private chooseBossBlockedDirection(enemy: TankEntity, target: Vec2, allowReverse: boolean): Direction {
    const forward = enemy.direction;
    const preferred = this.chooseBossLateralDirection(enemy, target);
    const alternate = preferred === leftTurn(forward) ? rightTurn(forward) : leftTurn(forward);
    const candidates = allowReverse
      ? [preferred, alternate, forward, oppositeDirection(forward)]
      : [preferred, alternate, forward];
    return candidates.find((direction) => this.canEnemyMoveToward(enemy, direction)) ?? candidates[candidates.length - 1];
  }

  private chooseBossLateralDirection(enemy: TankEntity, target: Vec2): Direction {
    const left = leftTurn(enemy.direction);
    const right = rightTurn(enemy.direction);
    const leftProbe = DIR_VECTORS[left];
    const rightProbe = DIR_VECTORS[right];
    const leftDistance = Math.hypot(enemy.x + leftProbe.x * CELL_SIZE - target.x, enemy.y + leftProbe.y * CELL_SIZE - target.y);
    const rightDistance = Math.hypot(enemy.x + rightProbe.x * CELL_SIZE - target.x, enemy.y + rightProbe.y * CELL_SIZE - target.y);
    if (Math.abs(leftDistance - rightDistance) < 0.01) return enemy.turnBias < 0 ? left : right;
    return leftDistance <= rightDistance ? left : right;
  }

  private lineOfSightDirectionToPoint(enemy: TankEntity, target: Vec2, tolerance: number): Direction | null {
    let direction: Direction | null = null;
    if (Math.abs(enemy.x - target.x) < tolerance) direction = target.y < enemy.y ? 'up' : 'down';
    else if (Math.abs(enemy.y - target.y) < tolerance) direction = target.x < enemy.x ? 'left' : 'right';
    if (!direction) return null;

    const vec = DIR_VECTORS[direction];
    let row = Math.floor(enemy.y / CELL_SIZE);
    let col = Math.floor(enemy.x / CELL_SIZE);
    const targetRow = Math.floor(target.y / CELL_SIZE);
    const targetCol = Math.floor(target.x / CELL_SIZE);
    for (let step = 0; step < Math.max(ROWS, COLS); step += 1) {
      row += vec.y;
      col += vec.x;
      if (row < 0 || row >= ROWS || col < 0 || col >= COLS) return null;
      if (row === targetRow && col === targetCol) return direction;
      if (tileBlocksBullet(this.map[row][col])) return null;
    }
    return null;
  }

  private tryFire(tank: TankEntity): void {
    if (!tank.alive || tank.fireCooldown > 0 || tank.fireBuffer <= 0 || tank.freezeTimer > 0 || tank.jamTimer > 0) return;
    const vec = vectorFor(tank.direction);
    const muzzle = tank.size / 2 + 8;
    const specialShellKind = this.getSpecialShellKind(tank);
    const armorPiercing = specialShellKind === 'armorPiercing';
    const explosive = specialShellKind === 'explosive';
    const tracer = specialShellKind === 'tracer';
    const muzzlePoint = this.getMuzzleOrigin(tank, vec, muzzle);
    const bossVariant = tank.kind === 'boss' ? this.getBossVariantProfile(tank.bossVariant) : null;
    const armorPiercingShell = armorPiercing ? this.getArmorPiercingShellSpec(tank) : null;
    const explosiveShell = explosive ? this.getExplosiveShellSpec(tank) : null;
    const tracerShell = tracer ? this.getTracerShellSpec(tank) : null;
    const bulletSpeedMultiplier = tracerShell?.speedMultiplier ?? 1;
    const bullet: BulletEntity = {
      id: makeId('bullet'),
      ownerTeam: tank.team,
      ownerPlayer: tank.playerNum,
      x: muzzlePoint.x,
      y: muzzlePoint.y,
      vx: vec.x * tank.bulletSpeed * bulletSpeedMultiplier,
      vy: vec.y * tank.bulletSpeed * bulletSpeedMultiplier,
      direction: tank.direction,
      size: armorPiercingShell
        ? BULLET_SIZE + armorPiercingShell.sizeBonus
        : explosiveShell
          ? BULLET_SIZE + explosiveShell.sizeBonus
          : tracerShell
            ? BULLET_SIZE + tracerShell.sizeBonus
            : tank.team === 'enemy' && tank.kind === 'boss'
              ? BULLET_SIZE + 2.4 + (bossVariant?.shellSizeBonus ?? 0)
              : tank.team === 'player' && tank.upgradeLevel >= 3
                ? BULLET_SIZE + 1
                : BULLET_SIZE,
      damage: armorPiercingShell
        ? Math.max(getTankBulletDamage(tank) * armorPiercingShell.damageMultiplier, armorPiercingShell.minDamage)
        : explosiveShell
          ? Math.max(getTankBulletDamage(tank) * explosiveShell.damageMultiplier, explosiveShell.minDamage)
          : tracerShell
            ? Math.max(getTankBulletDamage(tank) * tracerShell.damageMultiplier, tracerShell.minDamage)
            : getTankBulletDamage(tank),
      armorPiercing,
      pierceBricksRemaining: armorPiercingShell?.brickPierce ?? 0,
      pierceMetalRemaining: armorPiercingShell?.metalPierce ?? 0,
      explosive,
      tracer,
      suppressSeconds: tracerShell?.suppressSeconds,
      blastRadius: explosiveShell?.radius,
      blastDamage: explosiveShell?.splashDamage,
      blastLightDamage: explosiveShell?.lightSplashDamage,
      blastTileDamage: explosiveShell?.tileDamage,
      alive: true,
      ttl: 2.8,
    };
    this.bullets.push(bullet);
    tank.fireCooldown = tank.fireRate;
    tank.fireBuffer = 0;
    this.events.push({ kind: 'shoot', x: bullet.x, y: bullet.y, shootVariant: tank.kind === 'boss' ? 'boss' : 'standard' });
  }

  private getMuzzleOrigin(tank: TankEntity, vec: Vec2, muzzleDistance: number): Vec2 {
    const origin = {
      x: tank.x + vec.x * muzzleDistance,
      y: tank.y + vec.y * muzzleDistance,
    };
    const lateralOffset = getSpriteMuzzleLateralOffset(tank);
    if (tank.direction === 'up' || tank.direction === 'down') origin.x += lateralOffset;
    else origin.y += lateralOffset;
    return origin;
  }

  private getSpecialShellKind(tank: TankEntity): SpecialShellKind | null {
    if (tank.team === 'enemy' && tank.kind === 'boss') {
      const cadence = this.getBossVariantProfile(tank.bossVariant).armorPiercingCadence;
      if (cadence <= 0) return null;
      tank.specialShotCounter = (tank.specialShotCounter + 1) % cadence;
      return tank.specialShotCounter === 0 ? 'armorPiercing' : null;
    }
    if (tank.team !== 'player' || tank.upgradeLevel < 3) return null;
    if (tank.playerTankClass === 'assault') {
      const maxLevel = tank.upgradeLevel >= MAX_PLAYER_UPGRADE_LEVEL;
      const cycleLength = 5;
      const tracerStart = maxLevel ? 3 : 4;
      const shotIndex = tank.specialShotCounter % cycleLength;
      tank.specialShotCounter = (shotIndex + 1) % cycleLength;
      return shotIndex >= tracerStart ? 'tracer' : null;
    }
    if (tank.playerTankClass === 'heavy') {
      const maxLevel = tank.upgradeLevel >= MAX_PLAYER_UPGRADE_LEVEL;
      const cycleLength = maxLevel ? 4 : 6;
      const explosiveStart = maxLevel ? 2 : 4;
      const shotIndex = tank.specialShotCounter % cycleLength;
      tank.specialShotCounter = (shotIndex + 1) % cycleLength;
      return shotIndex >= explosiveStart ? 'explosive' : null;
    }
    const cadence = tank.upgradeLevel >= MAX_PLAYER_UPGRADE_LEVEL ? 4 : 6;
    tank.specialShotCounter = (tank.specialShotCounter + 1) % cadence;
    if (tank.specialShotCounter !== 0) return null;
    return 'armorPiercing';
  }

  private getArmorPiercingShellSpec(tank: TankEntity): typeof PLAYER_ARMOR_PIERCING_SHELL {
    if (tank.team === 'enemy' && tank.kind === 'boss') {
      const variant = this.getBossVariantProfile(tank.bossVariant);
      return {
        damageMultiplier: variant.armorPiercingDamageMultiplier,
        minDamage: variant.bulletDamage,
        brickPierce: variant.armorPiercingBrickPierce,
        metalPierce: variant.armorPiercingMetalPierce,
        sizeBonus: 2.8 + variant.shellSizeBonus,
      };
    }
    return PLAYER_ARMOR_PIERCING_SHELL;
  }

  private getExplosiveShellSpec(tank: TankEntity): typeof PLAYER_EXPLOSIVE_SHELLS.level3 {
    return tank.upgradeLevel >= MAX_PLAYER_UPGRADE_LEVEL ? PLAYER_EXPLOSIVE_SHELLS.level4 : PLAYER_EXPLOSIVE_SHELLS.level3;
  }

  private getTracerShellSpec(tank: TankEntity): typeof PLAYER_TRACER_SHELLS.level3 {
    return tank.upgradeLevel >= MAX_PLAYER_UPGRADE_LEVEL ? PLAYER_TRACER_SHELLS.level4 : PLAYER_TRACER_SHELLS.level3;
  }

  private updateBullets(dt: number): void {
    for (const bullet of this.bullets) {
      if (!bullet.alive) continue;
      bullet.ttl -= dt;
      if (bullet.ttl <= 0) {
        bullet.alive = false;
        continue;
      }
      const distanceThisFrame = Math.hypot(bullet.vx * dt, bullet.vy * dt);
      const steps = Math.max(1, Math.ceil(distanceThisFrame / (CELL_SIZE * 0.28)));
      for (let step = 0; step < steps && bullet.alive; step += 1) {
        bullet.x += (bullet.vx * dt) / steps;
        bullet.y += (bullet.vy * dt) / steps;
        if (bullet.x < -16 || bullet.x > VIEW_WIDTH + 16 || bullet.y < -16 || bullet.y > VIEW_HEIGHT + 16) {
          bullet.alive = false;
          break;
        }
        this.handleBulletTileHit(bullet);
        if (bullet.alive) this.handleBulletTankHit(bullet);
      }
    }
    this.bullets = this.bullets.filter((bullet) => bullet.alive);
  }

  private handleBulletTileHit(bullet: BulletEntity): void {
    const col = Math.floor(bullet.x / CELL_SIZE);
    const row = Math.floor(bullet.y / CELL_SIZE);
    if (row < 0 || row >= this.map.length || col < 0 || col >= this.map[row].length) return;
    const tile = this.map[row][col];
    if (!tileBlocksBullet(tile)) return;
    const center = { x: col * CELL_SIZE + CELL_SIZE / 2, y: row * CELL_SIZE + CELL_SIZE / 2 };

    if (tile === TileType.Brick) {
      this.map[row][col] = TileType.Empty;
      if (bullet.explosive) {
        bullet.alive = false;
      } else if (bullet.armorPiercing && (bullet.pierceBricksRemaining ?? 0) > 0) {
        bullet.pierceBricksRemaining = (bullet.pierceBricksRemaining ?? 0) - 1;
      } else {
        bullet.alive = false;
      }
      this.score += 10;
      this.addBattleDecal('scorch', center.x, center.y, 34);
      this.spawnDebris(center.x, center.y, 0xc88955, 10);
      this.maybeSpawnPowerup(center.x, center.y, 0.035, 'terrain');
      this.events.push({ kind: 'hit', x: center.x, y: center.y, hitVariant: 'terrain' });
      if (bullet.explosive) this.detonateExplosiveBullet(bullet, center.x, center.y);
      return;
    }
    if (tile === TileType.ReinforcedWall || tile === TileType.BaseWall) {
      const isBaseWall = tile === TileType.BaseWall;
      if (isBaseWall && bullet.ownerTeam === 'player' && !this.mode.friendlyFire) {
        bullet.alive = false;
        this.spawnDebris(center.x, center.y, 0x66e6ff, 6);
        this.events.push({ kind: 'hit', x: center.x, y: center.y, hitVariant: 'shield' });
        return;
      }
      if (isBaseWall && this.isBaseProtected()) {
        bullet.alive = false;
        this.spawnDebris(center.x, center.y, 0x66e6ff, 8);
        this.events.push({ kind: 'hit', x: center.x, y: center.y, hitVariant: 'shield' });
        return;
      }
      const canPierceSteel = tile === TileType.ReinforcedWall && bullet.armorPiercing && (bullet.pierceMetalRemaining ?? 0) > 0;
      if (!canPierceSteel) bullet.alive = false;
      const tileDamage = canPierceSteel ? this.getTileMaxHp(tile) : bullet.damage;
      if (this.damageTile(row, col, tileDamage)) {
        this.map[row][col] = TileType.Empty;
        this.tileHp.delete(tileKey(row, col));
        this.addBattleDecal('scorch', center.x, center.y, tile === TileType.ReinforcedWall ? 42 : 36);
        this.spawnDebris(center.x, center.y, tile === TileType.ReinforcedWall ? 0xaeb7b2 : 0xd0aa70, 12);
        if (tile === TileType.ReinforcedWall) this.score += 20;
        if (canPierceSteel) bullet.pierceMetalRemaining = (bullet.pierceMetalRemaining ?? 0) - 1;
      } else {
        this.addBattleDecal('scorch', center.x, center.y, 26);
        this.spawnDebris(center.x, center.y, tile === TileType.ReinforcedWall ? 0xb9c1bc : 0xd0aa70, 7);
      }
      this.events.push({ kind: 'hit', x: center.x, y: center.y, hitVariant: 'terrain' });
      if (bullet.explosive) this.detonateExplosiveBullet(bullet, center.x, center.y);
      return;
    }
    if (tile === TileType.BaseCore) {
      bullet.alive = false;
      if (bullet.ownerTeam === 'player' && !this.mode.friendlyFire) {
        this.spawnDebris(center.x, center.y, 0x66e6ff, 6);
        this.events.push({ kind: 'hit', x: center.x, y: center.y, hitVariant: 'shield' });
        return;
      }
      if (this.isBaseProtected()) {
        this.spawnDebris(center.x, center.y, 0x66e6ff, 8);
        this.events.push({ kind: 'hit', x: center.x, y: center.y, hitVariant: 'shield' });
        return;
      }
      this.baseHp = Math.max(0, this.baseHp - bullet.damage);
      this.addBattleDecal('scorch', center.x, center.y, 38);
      this.spawnDebris(center.x, center.y, 0xffc456, 18);
      this.events.push({ kind: 'hit', x: center.x, y: center.y, hitVariant: 'base' });
      this.events.push({ kind: 'shake', amount: 8 });
      if (this.baseHp <= 0) {
        this.clearBaseCore();
        this.destroyBase();
      } else {
        this.pushMessage(t('message.baseDamaged', { hp: this.baseHp }));
      }
      if (bullet.explosive) this.detonateExplosiveBullet(bullet, center.x, center.y);
      return;
    }
    if (tile === TileType.Metal) {
      if (bullet.armorPiercing && (bullet.pierceMetalRemaining ?? 0) > 0) {
        bullet.pierceMetalRemaining = (bullet.pierceMetalRemaining ?? 0) - 1;
        this.map[row][col] = TileType.Empty;
        this.score += 25;
        this.addBattleDecal('scorch', center.x, center.y, 42);
        this.spawnDebris(center.x, center.y, 0xb7b8aa, 14);
        this.events.push({ kind: 'hit', x: center.x, y: center.y, hitVariant: 'terrain' });
        return;
      }
      bullet.alive = false;
      this.addBattleDecal('scorch', bullet.x, bullet.y, 28);
      this.spawnDebris(bullet.x, bullet.y, 0xb7b8aa, 5);
      this.events.push({ kind: 'hit', x: bullet.x, y: bullet.y, hitVariant: 'terrain' });
      if (bullet.explosive) this.detonateExplosiveBullet(bullet, bullet.x, bullet.y);
    }
  }

  private handleBulletTankHit(bullet: BulletEntity): void {
    const bulletRect = boundsAt(bullet.x, bullet.y, bullet.size * 2);
    const candidates = bullet.ownerTeam === 'player'
      ? this.enemies.filter((enemy) => enemy.alive)
      : this.players.filter((player) => player.alive);

    for (const tank of candidates) {
      if (!rectsOverlap(bulletRect, tankBoundsAt(tank.x, tank.y, tank.size))) continue;
      bullet.alive = false;
      if (tank.shieldTimer > 0) {
        this.spawnDebris(bullet.x, bullet.y, 0x66e6ff, 7);
        this.events.push({ kind: 'hit', x: bullet.x, y: bullet.y, hitVariant: 'shield' });
        return;
      }
      if (tank.invincibleTimer > 0 || (tank.team === 'player' && this.debugPlayerInvincible)) return;
      this.damageTank(tank, this.resolveDamage(tank, bullet));
      this.applyTracerSuppression(bullet, tank);
      if (bullet.explosive) this.detonateExplosiveBullet(bullet, bullet.x, bullet.y, tank.id);
      return;
    }

    if (bullet.ownerTeam === 'player' && this.mode.friendlyFire) {
      for (const target of this.players.filter((player) => player.playerNum !== bullet.ownerPlayer && player.alive)) {
        if (!rectsOverlap(bulletRect, tankBoundsAt(target.x, target.y, target.size))) continue;
        bullet.alive = false;
        if (target.shieldTimer > 0) {
          this.spawnDebris(bullet.x, bullet.y, 0x66e6ff, 7);
          this.events.push({ kind: 'hit', x: bullet.x, y: bullet.y, hitVariant: 'shield' });
          return;
        }
        if (target.invincibleTimer > 0 || (target.team === 'player' && this.debugPlayerInvincible)) return;
        this.damageTank(target, this.resolveDamage(target, bullet));
        this.applyTracerSuppression(bullet, target);
        if (bullet.explosive) this.detonateExplosiveBullet(bullet, bullet.x, bullet.y, target.id);
        return;
      }
    }
  }

  private applyTracerSuppression(bullet: BulletEntity, target: TankEntity): void {
    if (!bullet.tracer || bullet.ownerTeam !== 'player' || target.team !== 'enemy' || !target.alive) return;
    const baseSeconds = bullet.suppressSeconds ?? 0.55;
    const spec = bullet.ownerPlayer
      ? this.players.find((player) => player.playerNum === bullet.ownerPlayer)
      : undefined;
    const bossMultiplier = spec ? this.getTracerShellSpec(spec).bossSuppressMultiplier : PLAYER_TRACER_SHELLS.level3.bossSuppressMultiplier;
    const seconds = target.kind === 'boss' ? baseSeconds * bossMultiplier : baseSeconds;
    target.fireCooldown = Math.max(target.fireCooldown, seconds);
    target.fireBuffer = 0;
    if (target.shootWindupTimer > 0) {
      target.shootWindupTimer = target.kind === 'boss'
        ? Math.max(0, target.shootWindupTimer - seconds * 0.45)
        : 0;
    }
    target.aiTimer = Math.max(target.aiTimer, target.kind === 'boss' ? 0.12 : 0.24);
    this.spawnTracerImpact(target.x, target.y, target.kind === 'boss' ? 7 : 10);
  }

  private damageTank(tank: TankEntity, damage: number): void {
    tank.hp -= damage;
    if (tank.hp > 0 && tank.hp / tank.maxHp <= 0.5) {
      tank.smokeTimer = 0;
      this.spawnSmoke(tank.x, tank.y, tank.kind === 'boss' ? 5 : 4);
    }
    this.spawnDebris(tank.x, tank.y, tank.team === 'player' ? 0xf8d477 : 0xff835f, 8);
    if (tank.hp > 0) {
      this.events.push({
        kind: 'hit',
        x: tank.x,
        y: tank.y,
        hitVariant: tank.team === 'player' || tank.kind === 'heavy' || tank.kind === 'boss' ? 'armor' : 'impact',
      });
      return;
    }

    tank.alive = false;
    tank.vx = 0;
    tank.vy = 0;
    this.spawnExplosion(tank.x, tank.y, tank.team === 'player' ? 0xffc456 : 0xff6b3b, tank.kind === 'boss' ? 42 : 22);
    this.events.push({ kind: 'explode', x: tank.x, y: tank.y, amount: tank.kind === 'boss' ? 2 : 1 });
    this.events.push({ kind: 'shake', amount: tank.kind === 'boss' ? 10 : 5 });

    if (tank.team === 'enemy') {
      this.score += tank.scoreValue;
      this.maybeSpawnCombatPowerup(tank);
    } else {
      downgradePlayerOnDeath(tank, this.mode);
      tank.respawnTimer = PLAYER_RESPAWN_SECONDS;
      if (!this.mode.infiniteLives && tank.playerNum) this.setPlayerLives(tank.playerNum, Math.max(0, this.getPlayerLives(tank.playerNum) - 1));
    }
  }

  private resolveDamage(tank: TankEntity, bullet: BulletEntity): number {
    if (tank.team === 'enemy' && tank.kind === 'boss') {
      const normalPiercingFrontHit = bullet.ownerTeam === 'player' && bullet.armorPiercing;
      if (isFrontArmorHit(tank.direction, bullet.direction) && !normalPiercingFrontHit) {
        return bullet.damage * this.getBossVariantProfile(tank.bossVariant).frontArmorMultiplier;
      }
      return bullet.damage;
    }
    if (tank.team !== 'player') return bullet.damage;
    const stats = getPlayerUpgradeStats(tank);
    const classSpec = getPlayerTankClassSpec(tank);
    return isFrontArmorHit(tank.direction, bullet.direction)
      ? bullet.damage * stats.frontArmorMultiplier * classSpec.frontArmorMultiplier
      : bullet.damage;
  }

  private tryRespawnPlayer(index: number): void {
    const tank = this.players[index];
    const lives = tank.playerNum ? this.getPlayerLives(tank.playerNum) : 0;
    if (!this.mode.infiniteLives && lives <= 0) return;
    const spawn = findSafeSpawn(this.playerSpawnAt(index), this.map, this.liveTanks, TANK_SIZE);
    Object.assign(tank, {
      x: spawn.x,
      y: spawn.y,
      vx: 0,
      vy: 0,
      alive: true,
      hp: tank.maxHp,
      direction: 'up' as Direction,
      fireCooldown: 0,
      invincibleTimer: PLAYER_INVINCIBLE_SECONDS,
      shieldTimer: 0,
      freezeTimer: 0,
      maneuverTimer: 0,
      smokeTimer: 0,
      respawnTimer: 0,
      turnBias: 1 as -1 | 1,
    });
    this.pushMessage(t('message.respawn', { player: index + 1 }));
  }

  private updateSpawning(dt: number): void {
    if (this.enemiesToSpawn <= 0 || this.enemies.filter((enemy) => enemy.alive).length >= this.getMaxEnemiesOnScreen()) return;
    this.spawnTimer -= dt;
    if (this.spawnTimer > 0) return;
    const nextSpawn = this.spawnQueue[0];
    if (!nextSpawn) return;
    const archetype = ENEMY_ARCHETYPES[nextSpawn.kind];
    const spawn = findSafeSpawn(pick(this.levelDef.spawnPoints), this.map, this.liveTanks, archetype.size);
    if (canTankOccupy(this.map, spawn.x, spawn.y, archetype.size) && !collidesWithAnyTank(tankBoundsAt(spawn.x, spawn.y, archetype.size), this.liveTanks)) {
      this.enemies.push(this.createEnemy(archetype, spawn, nextSpawn.bossVariant));
      this.spawnQueue.shift();
      this.enemiesSpawned += 1;
      this.enemiesToSpawn -= 1;
      if (nextSpawn.kind === 'boss') {
        this.pushMessage(t('message.bossIncoming', {
          variant: t(this.getBossVariantProfile(nextSpawn.bossVariant).labelKey),
        }));
      }
    }
    this.spawnTimer = this.levelDef.spawnInterval * (0.72 + Math.random() * 0.5);
  }

  private updatePowerups(dt: number): void {
    for (const powerup of this.powerups) {
      powerup.life -= dt;
      powerup.bob += dt * 5;
      const rect = boundsAt(powerup.x, powerup.y + Math.sin(powerup.bob) * 3, powerup.size * 2);
      const player = this.players.find((candidate) => candidate.alive && rectsOverlap(rect, tankBoundsAt(candidate.x, candidate.y, candidate.size)));
      if (player) {
        this.applyPowerup(player, powerup.kind);
        powerup.life = -1;
      }
    }
    this.powerups = this.powerups.filter((powerup) => powerup.life > 0);
  }

  private applyPowerup(player: TankEntity, kind: PowerupKind): void {
    const labels: Record<PowerupKind, string> = {
      rapid: t('powerup.rapid'),
      shield: t('powerup.shield'),
      bomb: t('powerup.bomb'),
      life: t('powerup.life'),
      freeze: t('powerup.freeze'),
      repair: t('powerup.repair'),
      baseShield: t('powerup.baseShield'),
      jam: t('powerup.jam'),
      upgrade: t('powerup.upgrade'),
      heal: t('powerup.heal'),
    };
    if (kind === 'rapid') player.rapidTimer = POWERUP_SECONDS;
    if (kind === 'shield') player.shieldTimer = POWERUP_SECONDS;
    if (kind === 'life' && !this.mode.infiniteLives) {
      if (player.playerNum) this.setPlayerLives(player.playerNum, this.getPlayerLives(player.playerNum) + 1);
    }
    if (kind === 'bomb') {
      let affected = 0;
      for (const enemy of this.enemies) {
        if (!enemy.alive) continue;
        affected += 1;
        if (enemy.kind === 'boss') {
          const damage = Math.min(BOMB_BOSS_DAMAGE, Math.max(0, enemy.hp - 1));
          if (damage > 0) this.damageTank(enemy, damage);
          this.spawnExplosion(enemy.x, enemy.y, 0xff8f3d, 26);
          continue;
        }
        if (enemy.kind === 'heavy') {
          const damage = Math.max(0, enemy.hp - 1);
          if (damage > 0) this.damageTank(enemy, damage);
          this.spawnExplosion(enemy.x, enemy.y, 0xff8f3d, 18);
          continue;
        }
        enemy.alive = false;
        enemy.vx = 0;
        enemy.vy = 0;
        this.score += Math.floor(enemy.scoreValue * 0.7);
        this.spawnExplosion(enemy.x, enemy.y, 0xff8f3d, 16);
      }
      if (affected > 0) {
        this.events.push({ kind: 'explode', x: player.x, y: player.y });
        this.events.push({ kind: 'shake', amount: 8 });
      }
    }
    if (kind === 'freeze') {
      for (const enemy of this.enemies) enemy.freezeTimer = 4.2;
    }
    if (kind === 'repair') {
      this.repairBaseWalls();
      this.baseDestroyed = false;
      this.baseHp = 3;
    }
    if (kind === 'baseShield') this.baseShieldTimer = Math.max(this.baseShieldTimer, BASE_SHIELD_SECONDS);
    if (kind === 'jam') this.jamTimer = 5.5;
    if (kind === 'upgrade') upgradePlayerTank(player);
    if (kind === 'heal') player.hp = player.maxHp;
    this.pushMessage(t('message.powerupPickup', { powerup: labels[kind], player: player.playerNum ?? 1 }));
    this.events.push({ kind: 'powerup', x: player.x, y: player.y });
  }

  private maybeSpawnCombatPowerup(tank: TankEntity): void {
    if (tank.kind === 'boss') {
      this.spawnPowerup(tank.x, tank.y, this.pickPowerupKind('boss'));
      return;
    }
    if (this.trySpawnReservedUpgrade(tank.x, tank.y)) return;
    const hpFactor = this.players.some((player) => player.alive && player.hp / player.maxHp <= 0.5) ? 0.12 : 0;
    const baseFactor = this.isBaseDamaged() ? 0.1 : 0;
    const kindFactor = tank.kind === 'heavy' || tank.kind === 'sniper' ? 0.08 : tank.kind === 'assault' ? 0.04 : 0;
    this.maybeSpawnPowerup(tank.x, tank.y, 0.16 + hpFactor + baseFactor + kindFactor, 'combat');
  }

  private trySpawnReservedUpgrade(x: number, y: number): boolean {
    if (this.reservedUpgradeDrops <= 0 || this.mode.upgradeDropRate === 'off') return false;
    if (this.powerupDropCooldown > 0 || this.powerups.length >= this.getMaxPowerupsOnField()) return false;
    if (!this.canDropUpgrade()) {
      this.reservedUpgradeDrops = 0;
      return false;
    }
    this.reservedUpgradeDrops -= 1;
    this.spawnPowerup(x, y, 'upgrade');
    return true;
  }

  private maybeSpawnPowerup(x: number, y: number, chance: number, source: 'combat' | 'terrain'): void {
    if (this.powerupDropCooldown > 0) return;
    if (this.powerups.length >= this.getMaxPowerupsOnField()) chance *= 0.35;
    if (source === 'terrain' && this.powerups.length > 0) chance *= 0.4;
    if (Math.random() > chance) return;
    this.spawnPowerup(x, y, this.pickPowerupKind(source));
  }

  private spawnPowerup(x: number, y: number, kind: PowerupKind): void {
    this.powerups.push({
      id: makeId('powerup'),
      kind,
      x,
      y,
      size: 15,
      life: 18,
      bob: Math.random() * 10,
    });
    this.powerupDropCooldown = kind === 'bomb' || kind === 'repair' || kind === 'baseShield' || kind === 'upgrade' ? 5.5 : 3.8;
  }

  private pickPowerupKind(source: 'combat' | 'terrain' | 'boss'): PowerupKind {
    const lowPlayer = this.players.find((player) => player.alive && player.hp / player.maxHp <= 0.5);
    if (this.isBaseDamaged() && source !== 'terrain') return Math.random() > 0.5 ? 'repair' : 'baseShield';
    if (lowPlayer && Math.random() > 0.45) return 'heal';
    if (lowPlayer && !this.mode.infiniteLives && Math.random() > 0.45) return 'life';
    if (this.shouldDropUpgrade(source)) return 'upgrade';
    if (source === 'boss') return pick(['bomb', 'repair', 'baseShield', 'rapid', 'heal'] as PowerupKind[]);
    if (source === 'terrain') return pick(['rapid', 'shield', 'freeze', 'jam', 'heal'] as PowerupKind[]);
    return pick(['rapid', 'shield', 'freeze', 'jam', 'bomb', 'heal'] as PowerupKind[]);
  }

  private shouldDropUpgrade(source: 'combat' | 'terrain' | 'boss'): boolean {
    if (!this.canDropUpgrade()) return false;
    const multipliers = {
      off: 0,
      low: 0.55,
      normal: 1,
      high: 1.85,
    } as const;
    const baseChance = source === 'boss' ? 0.44 : source === 'combat' ? 0.18 : 0.055;
    return Math.random() < baseChance * multipliers[this.mode.upgradeDropRate] * this.getUpgradeDropScale();
  }

  private canDropUpgrade(): boolean {
    return this.players.some((player) => player.upgradeLevel < MAX_PLAYER_UPGRADE_LEVEL);
  }

  private getUpgradeDropScale(): number {
    if (this.mode.playerCount >= 4) return 0.74;
    if (this.mode.playerCount === 3) return 0.86;
    return 1;
  }

  private isBaseDamaged(): boolean {
    if (this.baseHp < 3) return true;
    for (let row = 0; row < ROWS; row += 1) {
      for (let col = 0; col < COLS; col += 1) {
        if (this.map[row][col] === TileType.BaseWall && this.getTileHp(row, col) < this.getTileMaxHp(TileType.BaseWall)) return true;
      }
    }
    return false;
  }

  private repairBaseWalls(): void {
    placeBase(this.map);
    this.initializeTileHp();
  }

  private destroyBase(): void {
    if (this.status !== 'playing') return;
    this.baseDestroyed = true;
    this.spawnExplosion(9.5 * CELL_SIZE, 13.5 * CELL_SIZE, 0xff4d34, 52);
    this.events.push({ kind: 'shake', amount: 18 });
    this.beginGameOver('baseDestroyed');
  }

  private checkNoLivesGameOver(): void {
    if (this.mode.infiniteLives || this.status !== 'playing') return;
    const allOut = this.players.every((player) => {
      const lives = player.playerNum ? this.getPlayerLives(player.playerNum) : 0;
      return lives <= 0 && !player.alive;
    });
    if (allOut) {
      this.beginGameOver('outOfLives');
    }
  }

  private beginGameOver(reason: 'baseDestroyed' | 'outOfLives'): void {
    if (this.status === 'gameOver') return;
    this.gameOverReason = reason;
    this.status = 'gameOver';
    this.events.push({ kind: 'gameOver', text: this.getGameOverReasonText() });
    this.pushMessage(this.getGameOverReasonText());
  }

  private checkLevelComplete(): void {
    if (this.status !== 'playing') return;
    if (this.enemiesToSpawn <= 0 && this.enemies.every((enemy) => !enemy.alive)) {
      this.status = 'levelComplete';
      this.levelTimer = this.mode.levelTransitionDelay;
      this.levelCountdownBeep = Math.min(3, Math.ceil(this.levelTimer));
      this.score += 500 + this.level * 25;
      this.events.push({ kind: 'level' });
    }
  }

  private updateLevelCountdownBeeps(): void {
    while (this.levelCountdownBeep > 0 && this.levelTimer <= this.levelCountdownBeep) {
      this.events.push({ kind: 'countdown', amount: this.levelCountdownBeep });
      this.levelCountdownBeep -= 1;
    }
  }

  private hasForwardLineOfSightToPlayer(enemy: TankEntity): boolean {
    const target = this.players.find((player) => player.alive && this.isAheadOnCurrentAxis(enemy, player, CELL_SIZE * 0.45));
    if (!target) return false;
    const minX = Math.min(enemy.x, target.x);
    const maxX = Math.max(enemy.x, target.x);
    const minY = Math.min(enemy.y, target.y);
    const maxY = Math.max(enemy.y, target.y);
    const rect = enemy.direction === 'left' || enemy.direction === 'right'
      ? { left: minX, right: maxX, top: enemy.y - 5, bottom: enemy.y + 5 }
      : { left: enemy.x - 5, right: enemy.x + 5, top: minY, bottom: maxY };
    const cells = cellsForRect(rect);
    for (let row = cells.startRow; row <= cells.endRow; row += 1) {
      for (let col = cells.startCol; col <= cells.endCol; col += 1) {
        if (tileBlocksBullet(this.map[row][col])) return false;
      }
    }
    return true;
  }

  private wantsForwardPlayerShot(enemy: TankEntity, dt: number): boolean {
    if (!this.hasForwardLineOfSightToPlayer(enemy)) return false;
    if (enemy.kind === 'boss' || enemy.kind === 'sniper') return true;
    return Math.random() < dt * this.getEnemyPlayerShotChance(enemy);
  }

  private hasForwardLineOfSightToExposedBaseCore(enemy: TankEntity): boolean {
    const vec = DIR_VECTORS[enemy.direction];
    let row = Math.floor(enemy.y / CELL_SIZE);
    let col = Math.floor(enemy.x / CELL_SIZE);
    for (let step = 0; step < Math.max(ROWS, COLS); step += 1) {
      row += vec.y;
      col += vec.x;
      if (row < 0 || row >= ROWS || col < 0 || col >= COLS) return false;
      const tile = this.map[row][col];
      if (!tileBlocksBullet(tile)) continue;
      return tile === TileType.BaseCore;
    }
    return false;
  }

  private wantsRandomClearShot(enemy: TankEntity, dt: number): boolean {
    if (enemy.fireCooldown > 0 || enemy.freezeTimer > 0 || enemy.jamTimer > 0) return false;
    if (!this.hasForwardDestructibleObstacle(enemy)) return false;
    const chancePerSecond = this.getEnemyClearShotChance(enemy);
    return Math.random() < dt * chancePerSecond;
  }

  private wantsBossScreenedPlayerShot(enemy: TankEntity, dt: number): boolean {
    if (enemy.kind !== 'boss' || enemy.fireCooldown > 0 || enemy.freezeTimer > 0 || enemy.jamTimer > 0) return false;
    const chancePerSecond = this.getBossVariantProfile(enemy.bossVariant).screenedPlayerShotChance;
    if (chancePerSecond <= 0) return false;
    if (!this.hasForwardDestructibleObstacleScreeningPlayer(enemy)) return false;
    return Math.random() < dt * chancePerSecond;
  }

  private tryBossProjectileDodge(enemy: TankEntity, dt: number): boolean {
    const variant = this.getBossVariantProfile(enemy.bossVariant);
    if (variant.evadeCooldownSeconds <= 0 || enemy.dodgeCooldownTimer > 0 || enemy.freezeTimer > 0 || enemy.jamTimer > 0) return false;
    const threat = this.findIncomingPlayerBullet(enemy, variant.evadeProjectileRange);
    if (!threat) return false;
    const dodgeDirection = this.chooseProjectileDodgeDirection(enemy, threat);
    if (!dodgeDirection) return false;
    enemy.debugAiLabel = 'EVADE';
    enemy.direction = dodgeDirection;
    enemy.aiDirection = dodgeDirection;
    enemy.aiTimer = Math.max(enemy.aiTimer, 0.22);
    enemy.dodgeCooldownTimer = variant.evadeCooldownSeconds;
    this.moveClassicTank(enemy, dodgeDirection, dt);
    return true;
  }

  private findIncomingPlayerBullet(enemy: TankEntity, range: number): BulletEntity | null {
    return this.bullets
      .filter((bullet) => bullet.alive && bullet.ownerTeam === 'player')
      .filter((bullet) => this.isBulletThreateningTank(bullet, enemy, range))
      .sort((a, b) => Math.hypot(a.x - enemy.x, a.y - enemy.y) - Math.hypot(b.x - enemy.x, b.y - enemy.y))[0] ?? null;
  }

  private isBulletThreateningTank(bullet: BulletEntity, enemy: TankEntity, range: number): boolean {
    const dx = enemy.x - bullet.x;
    const dy = enemy.y - bullet.y;
    const distance = Math.hypot(dx, dy);
    if (distance > range) return false;
    const tolerance = enemy.size * 0.72;
    if (bullet.direction === 'up') return Math.abs(dx) < tolerance && dy < 0;
    if (bullet.direction === 'down') return Math.abs(dx) < tolerance && dy > 0;
    if (bullet.direction === 'left') return Math.abs(dy) < tolerance && dx < 0;
    return Math.abs(dy) < tolerance && dx > 0;
  }

  private chooseProjectileDodgeDirection(enemy: TankEntity, bullet: BulletEntity): Direction | null {
    const candidates = bullet.direction === 'up' || bullet.direction === 'down'
      ? ([leftTurn(enemy.direction), rightTurn(enemy.direction), 'left', 'right'] as Direction[])
      : ([leftTurn(enemy.direction), rightTurn(enemy.direction), 'up', 'down'] as Direction[]);
    const ordered = enemy.turnBias < 0 ? candidates : [...candidates].reverse();
    return uniqueDirections(ordered).find((direction) => this.canEnemyMoveToward(enemy, direction)) ?? null;
  }

  private getEnemyShotWindup(enemy: TankEntity, closeRange = false): number {
    if (enemy.kind === 'boss') {
      const variant = this.getBossVariantProfile(enemy.bossVariant);
      return closeRange ? variant.closeShotWindup : variant.shotWindup;
    }
    if (enemy.kind === 'heavy') return 0.2;
    if (enemy.kind === 'sniper') return 0.1;
    if (enemy.kind === 'assault') return 0.14;
    return 0.12;
  }

  private getEnemyBlockedRetargetDelay(enemy: TankEntity): number {
    if (enemy.kind === 'heavy') return 0.54 + Math.random() * 0.46;
    if (enemy.kind === 'sniper') return 0.48 + Math.random() * 0.34;
    if (enemy.kind === 'assault') return 0.36 + Math.random() * 0.34;
    return 0.3 + Math.random() * 0.26;
  }

  private getEnemyClearShotChance(enemy: TankEntity): number {
    if (enemy.kind === 'boss') return 0.34;
    if (enemy.kind === 'heavy') return 0.28;
    if (enemy.kind === 'assault') return 0.14;
    if (enemy.kind === 'sniper') return 0.05;
    return 0.08;
  }

  private getEnemyPlayerShotChance(enemy: TankEntity): number {
    if (enemy.kind === 'heavy') return 1.25;
    if (enemy.kind === 'assault') return 1.1;
    if (enemy.kind === 'light') return 0.55;
    return 1;
  }

  private hasForwardDestructibleObstacle(enemy: TankEntity): boolean {
    const vec = DIR_VECTORS[enemy.direction];
    let row = Math.floor(enemy.y / CELL_SIZE);
    let col = Math.floor(enemy.x / CELL_SIZE);
    for (let step = 0; step < Math.max(ROWS, COLS); step += 1) {
      row += vec.y;
      col += vec.x;
      if (row < 0 || row >= ROWS || col < 0 || col >= COLS) return false;
      const tile = this.map[row][col];
      if (!tileBlocksBullet(tile)) continue;
      if (tile === TileType.Brick || tile === TileType.ReinforcedWall) return true;
      if (tile === TileType.BaseWall) return !this.isBaseProtected();
      return false;
    }
    return false;
  }

  private hasForwardDestructibleObstacleScreeningPlayer(enemy: TankEntity): boolean {
    const alignedPlayers = this.players
      .filter((player) => player.alive && this.isAheadOnCurrentAxis(enemy, player, CELL_SIZE * 0.42))
      .map((player) => `${Math.floor(player.y / CELL_SIZE)}:${Math.floor(player.x / CELL_SIZE)}`);
    if (alignedPlayers.length === 0) return false;
    const playerCells = new Set(alignedPlayers);
    const vec = DIR_VECTORS[enemy.direction];
    let row = Math.floor(enemy.y / CELL_SIZE);
    let col = Math.floor(enemy.x / CELL_SIZE);
    let hasDestructibleScreen = false;
    for (let step = 0; step < Math.max(ROWS, COLS); step += 1) {
      row += vec.y;
      col += vec.x;
      if (row < 0 || row >= ROWS || col < 0 || col >= COLS) return false;
      if (playerCells.has(`${row}:${col}`)) return hasDestructibleScreen;
      const tile = this.map[row][col];
      if (!tileBlocksBullet(tile)) continue;
      if (tile === TileType.Brick || tile === TileType.ReinforcedWall) {
        hasDestructibleScreen = true;
        continue;
      }
      return false;
    }
    return false;
  }

  private isAheadOnCurrentAxis(enemy: TankEntity, target: TankEntity, tolerance: number): boolean {
    if (enemy.direction === 'up') return Math.abs(enemy.x - target.x) < tolerance && target.y < enemy.y;
    if (enemy.direction === 'down') return Math.abs(enemy.x - target.x) < tolerance && target.y > enemy.y;
    if (enemy.direction === 'left') return Math.abs(enemy.y - target.y) < tolerance && target.x < enemy.x;
    return Math.abs(enemy.y - target.y) < tolerance && target.x > enemy.x;
  }

  private updateParticles(dt: number): void {
    for (const particle of this.particles) {
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.vy += 70 * dt;
      particle.life -= dt;
    }
    this.particles = this.particles.filter((particle) => particle.life > 0);
  }

  private updateDecals(dt: number): void {
    for (const decal of this.decals) decal.life -= dt;
    this.decals = this.decals.filter((decal) => decal.life > 0);
  }

  private updateEffects(dt: number): void {
    for (const effect of this.effects) {
      effect.life -= dt;
      const progress = 1 - Math.max(0, effect.life / effect.maxLife);
      effect.size = effect.maxSize * (effect.kind === 'flash' ? 1 - progress * 0.35 : progress);
    }
    this.effects = this.effects.filter((effect) => effect.life > 0);
  }

  private updateDamageSmoke(dt: number): void {
    for (const tank of this.liveTanks) {
      if (tank.hp / tank.maxHp > 0.5 || tank.invincibleTimer > 0) continue;
      tank.smokeTimer -= dt;
      if (tank.smokeTimer > 0) continue;
      const severity = 1 - tank.hp / tank.maxHp;
      this.spawnSmoke(tank.x, tank.y, tank.kind === 'boss' ? 5 : 3);
      tank.smokeTimer = Math.max(0.16, 0.46 - severity * 0.22);
    }
  }

  private spawnDebris(x: number, y: number, color: number, count: number): void {
    for (let i = 0; i < count; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 40 + Math.random() * 190;
      const life = 0.22 + Math.random() * 0.48;
      this.particles.push({
        id: makeId('particle'),
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color,
        size: 1.5 + Math.random() * 3,
        life,
        maxLife: life,
      });
    }
  }

  private spawnTracerImpact(x: number, y: number, count: number): void {
    for (let i = 0; i < count; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 70 + Math.random() * 240;
      const life = 0.16 + Math.random() * 0.22;
      this.particles.push({
        id: makeId('particle'),
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color: i % 3 === 0 ? 0xffffff : i % 2 === 0 ? 0xfff0a6 : 0xff5a24,
        size: 1.2 + Math.random() * 2.2,
        life,
        maxLife: life,
      });
    }
  }

  private detonateExplosiveBullet(bullet: BulletEntity, x: number, y: number, directTankId?: string): void {
    if (!bullet.explosive) return;
    const radius = bullet.blastRadius ?? CELL_SIZE;
    this.spawnShellExplosion(x, y, radius);
    this.applyExplosiveTileDamage(bullet, x, y, radius);

    const targets = bullet.ownerTeam === 'player'
      ? this.enemies.filter((enemy) => enemy.alive)
      : this.players.filter((player) => player.alive);
    for (const target of targets) {
      if (target.id === directTankId) continue;
      if (target.invincibleTimer > 0 || (target.team === 'player' && this.debugPlayerInvincible) || target.shieldTimer > 0) continue;
      const reach = radius + target.size * 0.32;
      const distance = Math.hypot(target.x - x, target.y - y);
      if (distance > reach) continue;
      const falloff = Math.max(0.38, 1 - (distance / reach) * 0.55);
      const isLightEnemy = target.team === 'enemy' && target.kind === 'light';
      let damage = isLightEnemy ? (bullet.blastLightDamage ?? 1.15) : (bullet.blastDamage ?? 0.7) * falloff;
      if (target.kind === 'boss') damage *= 0.42;
      this.damageTank(target, damage);
    }

    if (bullet.ownerTeam === 'player' && this.mode.friendlyFire) {
      for (const target of this.players.filter((player) => player.alive && player.playerNum !== bullet.ownerPlayer)) {
        if (target.invincibleTimer > 0 || target.shieldTimer > 0 || (target.team === 'player' && this.debugPlayerInvincible)) continue;
        const reach = radius + target.size * 0.25;
        const distance = Math.hypot(target.x - x, target.y - y);
        if (distance > reach) continue;
        const falloff = Math.max(0.3, 1 - (distance / reach) * 0.65);
        this.damageTank(target, (bullet.blastDamage ?? 0.7) * 0.55 * falloff);
      }
    }
  }

  private applyExplosiveTileDamage(bullet: BulletEntity, x: number, y: number, radius: number): void {
    const minCol = Math.max(0, Math.floor((x - radius) / CELL_SIZE));
    const maxCol = Math.min(COLS - 1, Math.floor((x + radius) / CELL_SIZE));
    const minRow = Math.max(0, Math.floor((y - radius) / CELL_SIZE));
    const maxRow = Math.min(ROWS - 1, Math.floor((y + radius) / CELL_SIZE));
    for (let row = minRow; row <= maxRow; row += 1) {
      for (let col = minCol; col <= maxCol; col += 1) {
        const tile = this.map[row][col];
        if (!tileBlocksBullet(tile) || tile === TileType.BaseCore || tile === TileType.Metal) continue;
        const isBaseWall = tile === TileType.BaseWall;
        if (isBaseWall && ((bullet.ownerTeam === 'player' && !this.mode.friendlyFire) || this.isBaseProtected())) continue;
        const centerX = col * CELL_SIZE + CELL_SIZE / 2;
        const centerY = row * CELL_SIZE + CELL_SIZE / 2;
        const distance = Math.hypot(centerX - x, centerY - y);
        if (distance > radius + CELL_SIZE * 0.24) continue;
        const falloff = Math.max(0.45, 1 - (distance / Math.max(1, radius)) * 0.5);
        const damage = (bullet.blastTileDamage ?? 1) * falloff;
        if (this.damageTile(row, col, damage)) {
          this.map[row][col] = TileType.Empty;
          this.tileHp.delete(tileKey(row, col));
          this.score += tile === TileType.ReinforcedWall ? 20 : 8;
          this.addBattleDecal('scorch', centerX, centerY, tile === TileType.ReinforcedWall ? 38 : 30);
          this.spawnDebris(centerX, centerY, tile === TileType.ReinforcedWall ? 0xaeb7b2 : 0xc88955, 7);
        }
      }
    }
  }

  private spawnShellExplosion(x: number, y: number, radius: number): void {
    this.addExplosionEffect('flash', x, y, 0xff8a2a, radius * 1.38, 0.16);
    this.addExplosionEffect('burst', x, y, 0xffe199, radius * 1.08, 0.2);
    this.addExplosionEffect('shockwave', x, y, 0xffb451, radius * 2.1, 0.32);
    this.addBattleDecal('crater', x, y, radius * 0.95);
    this.spawnDirectionalDebris(x, y, 0xff9b42, 9);
    this.spawnDebris(x, y, 0xffc56a, 18);
    this.spawnDebris(x, y, 0x5f4a36, 8);
    this.events.push({ kind: 'explode', x, y, amount: 0.85 });
    this.events.push({ kind: 'shake', amount: 4.5 });
  }

  private spawnExplosion(x: number, y: number, color: number, count: number): void {
    const large = count > 30;
    this.addExplosionEffect('flash', x, y, color, large ? 72 : 46, 0.18);
    this.addExplosionEffect('burst', x, y, 0xfff2a0, large ? 54 : 34, 0.22);
    this.addExplosionEffect('shockwave', x, y, 0xfff2a0, large ? 110 : 72, large ? 0.42 : 0.34);
    this.addBattleDecal('crater', x, y, large ? 66 : 48);
    this.spawnDirectionalDebris(x, y, color, Math.ceil(count * 0.45));
    this.spawnDebris(x, y, color, count);
    this.spawnDebris(x, y, 0xfff2a0, Math.ceil(count * 0.3));
  }

  private spawnDirectionalDebris(x: number, y: number, color: number, count: number): void {
    for (let i = 0; i < count; i += 1) {
      const direction = i % 4;
      const baseAngle = direction * (Math.PI / 2) + (Math.random() - 0.5) * 0.38;
      const speed = 140 + Math.random() * 160;
      const life = 0.28 + Math.random() * 0.34;
      this.particles.push({
        id: makeId('shard'),
        x,
        y,
        vx: Math.cos(baseAngle) * speed,
        vy: Math.sin(baseAngle) * speed,
        color,
        size: 2 + Math.random() * 3.5,
        life,
        maxLife: life,
      });
    }
  }

  private spawnSmoke(x: number, y: number, count: number): void {
    for (let i = 0; i < count; i += 1) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.1;
      const speed = 20 + Math.random() * 42;
      const life = 0.55 + Math.random() * 0.55;
      this.particles.push({
        id: makeId('smoke'),
        x: x + (Math.random() - 0.5) * 12,
        y: y + (Math.random() - 0.5) * 12,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 28,
        color: Math.random() > 0.45 ? 0x56564f : 0x2f312d,
        size: 4 + Math.random() * 6,
        life,
        maxLife: life,
      });
    }
  }

  private addBattleDecal(kind: BattleDecalEntity['kind'], x: number, y: number, size: number): void {
    this.decals.push({
      id: makeId('decal'),
      kind,
      x,
      y,
      size,
      rotation: Math.random() * Math.PI * 2,
      life: kind === 'crater' ? 28 : 14,
      maxLife: kind === 'crater' ? 28 : 14,
    });
    if (this.decals.length > 48) this.decals.splice(0, this.decals.length - 48);
  }

  private addExplosionEffect(kind: ExplosionEffectKind, x: number, y: number, color: number, maxSize: number, life: number): void {
    this.effects.push({
      id: makeId('effect'),
      kind,
      x,
      y,
      color,
      size: kind === 'shockwave' ? 0 : maxSize,
      maxSize,
      life,
      maxLife: life,
    });
    if (this.effects.length > 24) this.effects.splice(0, this.effects.length - 24);
  }

  private getBaseState(): string {
    if (this.mode.invincibleBase) return t('base.invincible');
    if (this.debugBaseInvincible) return t('base.shielded');
    if (this.baseShieldTimer > 0) return t('base.shielded');
    if (this.baseDestroyed) return t('base.destroyed');
    if (this.baseHp < 3) return `${this.baseHp}/3`;
    return t('base.stable');
  }

  private pushMessage(text: string): void {
    this.events.push({ kind: 'message', text });
  }

  private initializeTileHp(): void {
    this.tileHp.clear();
    for (let row = 0; row < ROWS; row += 1) {
      for (let col = 0; col < COLS; col += 1) {
        const maxHp = this.getTileMaxHp(this.map[row][col]);
        if (maxHp > 1 && this.map[row][col] !== TileType.BaseCore) this.tileHp.set(tileKey(row, col), maxHp);
      }
    }
  }

  isDebugBaseInvincible(): boolean {
    return this.debugBaseInvincible;
  }

  isDebugPlayerInvincible(): boolean {
    return this.debugPlayerInvincible;
  }

  private damageTile(row: number, col: number, damage: number): boolean {
    const tile = this.map[row][col];
    const maxHp = this.getTileMaxHp(tile);
    const nextHp = Math.max(0, (this.tileHp.get(tileKey(row, col)) ?? maxHp) - damage);
    this.tileHp.set(tileKey(row, col), nextHp);
    return nextHp <= 0;
  }

  private clearBaseCore(): void {
    for (let row = 0; row < ROWS; row += 1) {
      for (let col = 0; col < COLS; col += 1) {
        if (this.map[row][col] === TileType.BaseCore) this.map[row][col] = TileType.Empty;
      }
    }
  }
}

function isFrontArmorHit(tankDirection: Direction, bulletDirection: Direction): boolean {
  return (
    (tankDirection === 'up' && bulletDirection === 'down') ||
    (tankDirection === 'down' && bulletDirection === 'up') ||
    (tankDirection === 'left' && bulletDirection === 'right') ||
    (tankDirection === 'right' && bulletDirection === 'left')
  );
}

function clampLevelTransitionDelay(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_MODE.levelTransitionDelay;
  return Math.max(MIN_LEVEL_TRANSITION_DELAY, Math.min(MAX_LEVEL_TRANSITION_DELAY, value));
}

function clampPlayerUpgradeLevel(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_MODE.initialPlayerLevel;
  return Math.max(1, Math.min(MAX_PLAYER_UPGRADE_LEVEL, Math.round(value)));
}

function normalizePlayerTankClasses(value: GameModeConfig['playerTankClasses']): PlayerTankClass[] {
  return [
    normalizePlayerTankClass(value?.[0]),
    normalizePlayerTankClass(value?.[1]),
    normalizePlayerTankClass(value?.[2]),
    normalizePlayerTankClass(value?.[3]),
  ];
}

function controlsForPlayerInput(input: InputSnapshot, index: number): ControlState {
  if (index === 0) return input.p1;
  if (index === 1) return input.p2;
  if (index === 2) return input.p3;
  return input.p4;
}

function perpendicularDirections(direction: Direction): Direction[] {
  if (direction === 'up' || direction === 'down') return ['left', 'right'];
  return ['up', 'down'];
}

function oppositeDirection(direction: Direction): Direction {
  if (direction === 'up') return 'down';
  if (direction === 'down') return 'up';
  if (direction === 'left') return 'right';
  return 'left';
}

function leftTurn(direction: Direction): Direction {
  if (direction === 'up') return 'left';
  if (direction === 'down') return 'right';
  if (direction === 'left') return 'down';
  return 'up';
}

function rightTurn(direction: Direction): Direction {
  if (direction === 'up') return 'right';
  if (direction === 'down') return 'left';
  if (direction === 'left') return 'up';
  return 'down';
}

function uniqueDirections(directions: Direction[]): Direction[] {
  return directions.filter((direction, index) => directions.indexOf(direction) === index);
}

function tileKey(row: number, col: number): string {
  return `${row}:${col}`;
}
